import * as THREE from "three";
import { CSS2DObject, CSS2DRenderer } from "three/addons/renderers/CSS2DRenderer.js";
import {
  buildContinuousPath,
  bakeMapCollision,
  BANK_RANGE,
  CAMERA_DISTANCE,
  CAMERA_DISTANCE_MAX,
  CAMERA_DISTANCE_MIN,
  CAMERA_LERP,
  CAMERA_PITCH,
  CAMERA_PITCH_MAX,
  CAMERA_PITCH_MIN,
  CAMERA_YAW,
  createParkMap,
  FORGE_RANGE,
  INTERACT_RANGE,
  isPathsideBenchCell,
  isSidewalkLampCell,
  isWalkablePos,
  nearestInteractPoint,
  nearestWalkablePos,
  NPC_WANDER_INTERACT_RANGE,
  PREVIEW_CAMERA_TILE,
  RUN_ENERGY_DRAIN_PER_SEC,
  RUN_ENERGY_MAX,
  RUN_ENERGY_REGEN_PER_SEC,
  SKILLS,
  skillColorCss,
  Tile,
  tileAt,
  REACT_COOLDOWN_MS,
  type MapData,
  type NodeRuntime,
  type NpcKind,
  type PlayerPrivateState,
  type PlayerPublicState,
  type ResourceNodeDef,
  type ServerMessage,
  type SkillId,
  npcIsWanderer,
  npcProvidesService,
  isNpcFriend,
  npcBusyLine,
  npcColdLine,
  npcHiLine,
  npcMoodToward,
  npcOffersPet,
  npcPetOfferLine,
} from "@dogdell/shared";
import type { NetworkClient } from "../net/NetworkClient";
import type { Hud } from "../ui/Hud";
import { iconSvg } from "../ui/icons";
import { PerfHud } from "../ui/PerfHud";
import { Minimap } from "../ui/Minimap";
import { DogActor } from "./DogActor";
import { HoverHighlight, makePickVolume } from "./HoverHighlight";
import { HorizonDecor } from "./HorizonDecor";
import { AmbientVfx } from "./AmbientVfx";
import {
  applyLabelLod,
  NODE_LABEL_FAR,
  NODE_LABEL_NEAR,
  NPC_LABEL_FAR,
  NPC_LABEL_NEAR,
  PAD_LABEL_FAR,
  PAD_LABEL_NEAR,
  SHOP_ICO_FAR,
  SHOP_ICO_NEAR,
} from "./LabelLod";
import { ParkCritter, type DogPose } from "./ParkCritter";
import { ParkDuckFamily } from "./ParkDuckFamily";
import { ParkNpc } from "./ParkNpc";
import { ParkLeashWalker } from "./ParkLeashWalker";
import { ParkTennisMatch } from "./ParkTennis";
import { ParkBasketballMatch } from "./ParkBasketball";
import { ParkDrumCircle } from "./ParkDrumCircle";
import { ParkFishing } from "./ParkFishing";
import { ParkFrisbeeGame } from "./ParkFrisbee";
import { ParkPicnic, picnicKindFromId } from "./ParkPicnic";
import { ParkLawnSprayer } from "./ParkLawnSprayer";
import { ParkStageShow, type StageActorSpot } from "./ParkStageShow";
import { terrainHeightAt, cellHeight } from "./Terrain";
import { InstancedVegetation, PARK_WIND_DIR_X, PARK_WIND_DIR_Z, parkWindSample, vegScale } from "./InstancedVegetation";
import { loadNatureAssets, natureTemplates } from "./NatureAssets";
import {
  loadFantasyTownAssets,
  cloneTownProp,
  cloneTownById,
  pickTownTemplate,
  townTemplates,
} from "./FantasyTownAssets";
import { loadCarAssets } from "./CarAssets";
import { loadRoadAssets } from "./RoadAssets";
import { loadToonCharacterAssets } from "./ToonCharacterAssets";
import { makeContactShadow } from "./ContactShadow";
import { billboardMaterial, faceCameraHorizontal, faceCameraYaw, facingFlipSign, makeSilhouette, placeRimBehind, setSilhouetteMap, SPRITE_FOOT_Y, WorldArt } from "./WorldArt";
import { humanSprite, furnitureSprite, isFurnitureSprite, HUMAN_LOOK_COUNT, type FurnitureKind, type HumanPose } from "./CharacterSprites";
import { makeProp, makeTravelPad, makeTravelPortal, makeTeleportBurst, makeHarvestBurst, STAGE_DECK_TOP, benchSeatY, type PropKind } from "./WorldModels";

export interface ParkViewData {
  net: NetworkClient;
  hud: Hud;
  welcome?: Extract<ServerMessage, { type: "welcome" }>;
  spectator?: Extract<ServerMessage, { type: "spectatorWelcome" }>;
}

interface Billboard {
  mesh: THREE.Mesh;
  /** Soft cream rim behind the sprite. */
  rim?: THREE.Mesh;
  baseY: number;
  phase: number;
  bob: boolean;
  /** World facing (radians) for camera-relative left/right flip. */
  facing: number;
  worldX: number;
  worldZ: number;
  /**
   * Horizontal nudge toward (+) / away from (−) the camera so layered sprites
   * (bench + sitter) don't share a depth plane.
   */
  cameraDepth?: number;
  /** NPC dogs cycle idle frames. */
  npcKind?: NpcKind;
  frame?: number;
  /** Sync silhouette when idle frames advance. */
  highlight?: HoverHighlight;
}

interface WorldInteractable {
  id: string;
  hit: THREE.Object3D;
  highlight: HoverHighlight;
  /** Tile footprint for ground-hover fallback. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Client-only talk line for wandering flavor NPCs. */
  talkLine?: string;
}

/** Idle chatter from humans sitting on park benches. */
interface BenchChatter {
  el: HTMLDivElement;
  object: CSS2DObject;
  worldX: number;
  worldZ: number;
  /** When to show the next line (ms, performance.now). */
  nextAt: number;
  /** When to hide the current line. */
  hideAt: number;
  seed: number;
  lines: readonly string[];
  holdMs: number;
}

/** Proximity hello / pet-offer bubbles from park humans. */
interface SocialReactor {
  nodeId: string;
  el: HTMLDivElement;
  object: CSS2DObject;
  worldX: number;
  worldZ: number;
  hideAt: number;
  nextAt: number;
}

/** Soft park-day musings from bench sitters. */
const BENCH_CHATTER_LINES = [
  "What a lovely day!",
  "All these dogs are so cute…",
  "I could sit here forever.",
  "Look at that happy tail!",
  "Fresh air and good pups.",
  "Someone deserves a treat.",
  "Isn't this park the best?",
  "Aww, so polite!",
  "The sun feels wonderful.",
  "Best seats in the park.",
  "I love watching them play.",
  "Such soft little faces!",
  "Every pup is a delight.",
  "Perfect day for a stroll.",
  "That one's got springy paws!",
  "They make the park brighter.",
];

/** Only one idle chatter bubble within this radius (tiles) - amph crowds stay readable. */
const CHATTER_EXCLUSIVE_R = 11;
const CHATTER_EXCLUSIVE_R2 = CHATTER_EXCLUSIVE_R * CHATTER_EXCLUSIVE_R;

/** Cookout pavilion chatter - grilling is only part of the scene. */
const COOKOUT_CHATTER_LINES = [
  "Pass the mustard!",
  "Smell that charcoal?",
  "Who wants another burger?",
  "Save a bite for the polite pups.",
  "Picnic weather is perfect.",
  "Don't forget the cooler!",
  "These dogs have great manners.",
  "Grill's almost ready…",
  "Chips are for sharing, right?",
  "Best cookout in Dogdell Park.",
  "Hold my lemonade...",
  "Someone's got hungry eyes!",
];

/**
 * RS2-style park: perspective 3D ground, 2D sprites standing on it.
 */
export class ParkView {
  private readonly root: HTMLElement;
  private readonly net: NetworkClient;
  private readonly hud: Hud;
  private readonly art = new WorldArt();
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly labels: CSS2DRenderer;
  private readonly raycaster = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();
  private readonly groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private readonly groundHitScratch = new THREE.Vector3();
  private readonly worldRoot = new THREE.Group();
  private readonly dogs = new Map<string, DogActor>();
  private readonly nodeByTile = new Map<string, ResourceNodeDef>();
  private readonly billboards: Billboard[] = [];
  private readonly critters: ParkCritter[] = [];
  private readonly duckFamilies: ParkDuckFamily[] = [];
  private readonly parkNpcs: ParkNpc[] = [];
  private readonly leashWalkers: ParkLeashWalker[] = [];
  private readonly tennisMatches: ParkTennisMatch[] = [];
  private readonly basketballMatches: ParkBasketballMatch[] = [];
  private readonly drumCircles: ParkDrumCircle[] = [];
  private readonly fishingSpots: ParkFishing[] = [];
  private readonly frisbeeGames: ParkFrisbeeGame[] = [];
  private readonly picnics: ParkPicnic[] = [];
  private readonly lawnSprayers: ParkLawnSprayer[] = [];
  private horizon: HorizonDecor | null = null;
  private ambientVfx: AmbientVfx | null = null;
  private vegetation: InstancedVegetation | null = null;
  private readonly portalFx: THREE.Object3D[] = [];
  /** Pond surface mesh - UV scroll + soft vertex bob. */
  private waterMesh: THREE.Mesh | null = null;
  private waterBasePositions: Float32Array | null = null;
  /** Raised pad tops (travel portals) so paws don't sink through concrete. */
  private readonly walkSurfaces: { x: number; z: number; radius: number; surfaceY: number }[] = [];
  private readonly teleportBursts: THREE.Group[] = [];
  private readonly harvestBursts: THREE.Group[] = [];
  /** Last dig/fetch/forage node the local pup started - for harvest sparkles. */
  private lastHarvestNodeId: string | null = null;
  private readonly swayProps: {
    root: THREE.Object3D;
    x: number;
    z: number;
    soft: boolean;
    baseScale: number;
  }[] = [];
  private readonly worldLabels: { object: CSS2DObject; kind: "npc" | "node" | "pad" | "ico" }[] = [];
  private readonly interactables: WorldInteractable[] = [];
  /** Dig / fetch / forage prop roots for depleted fade. */
  private readonly gatherVisuals = new Map<string, { root: THREE.Object3D; iconEl?: HTMLElement }>();
  private readonly depletedNodes = new Set<string>();
  private readonly benchChatters: BenchChatter[] = [];
  private readonly socialReactors: SocialReactor[] = [];
  private stageShow: ParkStageShow | null = null;
  private readonly clickMarker: THREE.Mesh;
  private readonly waypointFlag: THREE.Group;
  private readonly waypointHoist: THREE.Group;
  private readonly waypointBanner: THREE.Mesh;
  private readonly waypointBannerRest: Float32Array;
  /** Soft glow ring shown while walking to a teleporter (no bone flag). */
  private readonly travelRing: THREE.Group;
  private readonly travelRingOuter: THREE.Mesh;
  /** Yellow glow ring while walking to a harvestable node. */
  private readonly harvestRing: THREE.Group;
  private readonly harvestRingOuter: THREE.Mesh;
  private map!: MapData;
  private nodes: ResourceNodeDef[] = [];
  private localId = "";
  private you: PlayerPrivateState | null = null;
  private previewMode = true;
  private previewFocus: { x: number; y: number } = { ...PREVIEW_CAMERA_TILE };
  private queuePlayers: PlayerPublicState[] = [];
  private focus = new THREE.Vector3();
  private yaw = CAMERA_YAW;
  private pitch = CAMERA_PITCH;
  private distance = CAMERA_DISTANCE;
  private orbiting = false;
  /** Left-button press that may become a camera drag once moved past the click threshold. */
  private pendingLeftOrbit = false;
  private pointerDirty = false;
  private lastPointerX = 0;
  private lastPointerY = 0;
  private downX = 0;
  private downY = 0;
  private spaceDown = false;
  /** Hold Shift to jog. */
  private shiftRun = false;
  /** Sticky Run orb toggle. */
  private runToggle = false;
  private lastSentRunning: boolean | null = null;
  /** Client-side gate so spray clusters don't spam react packets. */
  private lastReactSentAt = 0;
  /** Predicted stamina; corrected from `you.energy` each state tick. */
  private localEnergy = RUN_ENERGY_MAX;
  /** Cooldown between flower-bed stir bursts. */
  private flowerStirIn = 0;
  private pinchStartDist = 0;
  private pinchStartDistance = CAMERA_DISTANCE;
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private clickFade = 0;
  private walkTarget: {
    x: number;
    y: number;
    surfaceY: number;
    /** `travel` / `harvest` = glowing ring instead of the bone flag. */
    mode?: "flag" | "travel" | "harvest";
    radius?: number;
    /** Ring anchor when the walk pose differs (harvestables). */
    ringX?: number;
    ringZ?: number;
    ringSurfaceY?: number;
  } | null = null;
  private hoveredDogId: string | null = null;
  private hoveredInteractableId: string | null = null;
  /** Stays outlined after click until another selection or a ground move. */
  private selectedInteractableId: string | null = null;
  private frame = 0;
  private lastTime = performance.now();
  private readonly perf: PerfHud;
  private readonly minimap: Minimap;
  private pixelRatioCap = 1.5;
  private qualityCooldown = 0;
  private frameMsEma = 16.6;
  private unsubscribe: (() => void) | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private ready = false;

  constructor(root: HTMLElement, data: ParkViewData) {
    this.root = root;
    this.net = data.net;
    this.hud = data.hud;
    this.perf = new PerfHud(root);
    this.minimap = new Minimap(root);
    this.minimap.runButton.hidden = true;
    this.applyInit(data);
    this.syncPreviewChrome();

    // Soft golden-hour postcard - warmer amber sky, peach haze, filmic dusk light.
    this.scene.background = new THREE.Color(0xffd6a8);
    this.scene.fog = new THREE.Fog(0xffc090, 55, 280);
    this.scene.add(new THREE.HemisphereLight(0xfff2dc, 0xb8c868, 1.18));
    const sun = new THREE.DirectionalLight(0xffc084, 1.12);
    sun.position.set(22, 18, 12);
    this.scene.add(sun);
    // Soft bounce from the lawn keeps shadows gentle and idyllic.
    const bounce = new THREE.DirectionalLight(0xffd8a8, 0.38);
    bounce.position.set(-12, 8, -10);
    this.scene.add(bounce);
    this.scene.add(this.worldRoot);

    this.camera = new THREE.PerspectiveCamera(48, 1, 0.12, 420);
    // Cap DPR: retina ×2 with antialias is the usual reason we miss 60.
    this.pixelRatioCap = Math.min(window.devicePixelRatio || 1, 1.25);
    this.renderer = new THREE.WebGLRenderer({
      antialias: false,
      alpha: false,
      powerPreference: "high-performance",
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.06;
    this.renderer.setPixelRatio(this.pixelRatioCap);
    this.renderer.domElement.className = "park-canvas";
    this.root.insertBefore(this.renderer.domElement, this.root.firstChild);

    const aura = document.createElement("div");
    aura.className = "park-aura";
    aura.setAttribute("aria-hidden", "true");
    this.root.insertBefore(aura, this.root.firstChild?.nextSibling ?? null);

    this.labels = new CSS2DRenderer();
    this.labels.domElement.className = "park-labels";
    this.root.appendChild(this.labels.domElement);

    const clickGeo = new THREE.PlaneGeometry(1.55, 1.55);
    clickGeo.rotateX(-Math.PI / 2);
    this.clickMarker = new THREE.Mesh(
      clickGeo,
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        depthWrite: false,
        opacity: 0,
        alphaTest: 0.15,
      }),
    );
    this.clickMarker.position.y = 0.03;
    this.clickMarker.visible = false;
    this.scene.add(this.clickMarker);

    this.waypointFlag = new THREE.Group();
    this.waypointFlag.visible = false;
    const poleH = 1.72;
    const flagH = 0.68;
    const pole = new THREE.Mesh(
      new THREE.CylinderGeometry(0.034, 0.044, poleH, 10, 1, true),
      new THREE.MeshBasicMaterial({
        color: 0x624826,
        depthWrite: true,
        depthTest: true,
        side: THREE.DoubleSide,
      }),
    );
    pole.position.y = poleH * 0.5;
    pole.renderOrder = 1;
    // Top of the cloth sits flush with the tip of the pole.
    this.waypointHoist = new THREE.Group();
    this.waypointHoist.position.set(0, poleH, 0);
    const flagW = 1.05;
    const bannerGeo = new THREE.PlaneGeometry(flagW, flagH, 12, 5);
    bannerGeo.translate(flagW * 0.5, -flagH * 0.5, 0);
    this.waypointBannerRest = new Float32Array(bannerGeo.attributes.position!.array as Float32Array);
    this.waypointBanner = new THREE.Mesh(
      bannerGeo,
      new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 1,
        alphaTest: 0.2,
        depthWrite: true,
        depthTest: true,
        side: THREE.DoubleSide,
      }),
    );
    this.waypointBanner.renderOrder = 1;
    this.waypointHoist.add(this.waypointBanner);
    this.waypointFlag.add(pole, this.waypointHoist);
    this.scene.add(this.waypointFlag);

    // Destination ring for teleporters (shown instead of the bone flag).
    this.travelRing = new THREE.Group();
    this.travelRing.visible = false;
    this.travelRingOuter = new THREE.Mesh(
      new THREE.RingGeometry(0.96, 1.08, 48),
      new THREE.MeshBasicMaterial({
        color: 0xb3f0ff,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        toneMapped: false,
      }),
    );
    this.travelRingOuter.rotation.x = -Math.PI / 2;
    this.travelRing.add(this.travelRingOuter);
    this.scene.add(this.travelRing);

    this.harvestRing = new THREE.Group();
    this.harvestRing.visible = false;
    this.harvestRingOuter = new THREE.Mesh(
      new THREE.RingGeometry(0.96, 1.08, 48),
      new THREE.MeshBasicMaterial({
        color: 0xfff176,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        toneMapped: false,
      }),
    );
    this.harvestRingOuter.rotation.x = -Math.PI / 2;
    this.harvestRing.add(this.harvestRingOuter);
    this.scene.add(this.harvestRing);

    this.bindInput();
    this.resize();
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.root);

    this.unsubscribe = this.net.on((message) => this.onMessage(message));
    void this.boot();
  }

  enterPlay(welcome: Extract<ServerMessage, { type: "welcome" }>): void {
    this.previewMode = false;
    this.localId = welcome.playerId;
    this.you = welcome.you;
    this.localEnergy = typeof welcome.you.energy === "number" ? welcome.you.energy : RUN_ENERGY_MAX;
    this.replaceMap(welcome.map, welcome.nodes);
    this.applyNodeRuntime(welcome.nodeRuntime);
    for (const player of welcome.players) {
      this.upsertDog(player);
    }
    this.resetLoginCamera();
    this.hud.render(welcome.you);
    this.syncRun(true);
    this.syncPreviewChrome();
  }

  applyZone(message: Extract<ServerMessage, { type: "zoneChange" }>): void {
    this.you = message.you;
    if (typeof message.you.energy === "number") {
      this.localEnergy = message.you.energy;
    }
    this.clearWaypoint();
    // Cover before the map swap paints, in case leave FX hasn't arrived yet.
    if (!this.previewMode) {
      this.hud.beginTeleportCover();
    }
    this.replaceMap(message.map, message.nodes);
    this.applyNodeRuntime(message.nodeRuntime);
    this.pruneDogs(message.players);
    for (const player of message.players) {
      this.upsertDog(player);
    }
    this.snapFocusToLocal();
    this.hud.render(message.you);
    this.syncRun(true);
  }

  applySpectator(message: Extract<ServerMessage, { type: "spectatorWelcome" }>): void {
    this.previewMode = true;
    this.localId = "";
    this.you = null;
    this.shiftRun = false;
    this.runToggle = false;
    this.lastSentRunning = null;
    this.localEnergy = RUN_ENERGY_MAX;
    this.previewFocus = message.camera;
    this.replaceMap(message.map, message.nodes);
    for (const player of message.players) {
      this.upsertDog(player);
    }
    this.pruneDogs(message.players);
    this.focus.set(this.previewFocus.x + 0.5, 0.15, this.previewFocus.y + 0.5);
    this.syncRun();
    this.syncPreviewChrome();
  }

  /** Hide minimap / mark preview until the local pup has spawned. */
  private syncPreviewChrome(): void {
    this.root.classList.toggle("is-preview", this.previewMode);
    this.minimap.root.hidden = this.previewMode;
    if (this.previewMode) {
      this.minimap.runButton.hidden = true;
    }
  }

  resize(): void {
    const width = Math.max(2, this.root.clientWidth);
    const height = Math.max(2, this.root.clientHeight);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
    this.labels.setSize(width, height);
  }

  destroy(): void {
    cancelAnimationFrame(this.frame);
    this.unsubscribe?.();
    this.resizeObserver?.disconnect();
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.onWindowBlur);
    this.minimap.runButton.removeEventListener("click", this.onRunToggle);
    this.renderer.domElement.removeEventListener("pointerdown", this.onPointerDown);
    this.renderer.domElement.removeEventListener("pointermove", this.onPointerMove);
    this.renderer.domElement.removeEventListener("pointerup", this.onPointerUp);
    this.renderer.domElement.removeEventListener("pointercancel", this.onPointerUp);
    this.renderer.domElement.removeEventListener("wheel", this.onWheel);
    this.renderer.domElement.removeEventListener("contextmenu", this.onContextMenu);
    for (const dog of this.dogs.values()) {
      dog.destroy();
    }
    this.dogs.clear();
    this.clearCritters();
    this.clearWorld();
    this.perf.destroy();
    this.minimap.destroy();
    this.art.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
    this.labels.domElement.remove();
  }

  private applyInit(data: ParkViewData): void {
    if (data.welcome) {
      this.map = bakeMapCollision(data.welcome.map);
      this.nodes = data.welcome.nodes;
      this.localId = data.welcome.playerId;
      this.you = data.welcome.you;
      this.previewMode = false;
      this.queuePlayers = data.welcome.players;
    } else if (data.spectator) {
      this.map = bakeMapCollision(data.spectator.map);
      this.nodes = data.spectator.nodes;
      this.previewMode = true;
      this.queuePlayers = data.spectator.players;
      this.previewFocus = data.spectator.camera;
    } else {
      const map = createParkMap();
      this.map = map;
      this.nodes = map.nodes;
      this.previewMode = true;
      this.queuePlayers = [];
      this.previewFocus = { ...PREVIEW_CAMERA_TILE };
    }
    this.focus.set(
      (this.previewMode ? this.previewFocus.x : (this.you?.x ?? this.previewFocus.x)) + 0.5,
      0.15,
      (this.previewMode ? this.previewFocus.y : (this.you?.y ?? this.previewFocus.y)) + 0.5,
    );
  }

  private async boot(): Promise<void> {
    await Promise.all([
      this.art.load(),
      loadNatureAssets(),
      loadFantasyTownAssets(),
      loadCarAssets(),
      loadRoadAssets(),
      loadToonCharacterAssets(),
    ]);
    const clickMat = this.clickMarker.material as THREE.MeshBasicMaterial;
    clickMat.map = this.art.sprite("click");
    clickMat.color.set(0xffffff);
    clickMat.needsUpdate = true;
    const flagMat = this.waypointBanner.material as THREE.MeshBasicMaterial;
    flagMat.map = this.art.sprite("waypoint");
    flagMat.opacity = 1;
    flagMat.needsUpdate = true;
    const anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
    for (const texture of this.art.tiles.values()) {
      texture.anisotropy = anisotropy;
    }
    this.buildWorld();
    for (const player of this.queuePlayers) {
      this.upsertDog(player);
    }
    this.ready = true;
    this.root.classList.add("is-scene-ready");
    if (this.you && !this.previewMode) {
      this.hud.render(this.you);
    }
    this.loop(performance.now());
  }

  private loop = (time: number): void => {
    const delta = Math.min(48, time - this.lastTime);
    this.lastTime = time;
    if (this.ready) {
      this.updateBillboards(time);
      this.updateClick(delta);
      this.updateWaypoint(time);
      this.updateCamera(delta);
      for (const dog of this.dogs.values()) {
        dog.update(time, this.camera, this.focus.x, this.focus.z);
      }
      const dogPoses = this.collectDogPoses();
      const dtSec = delta / 1000;
      for (const critter of this.critters) {
        critter.update(dtSec, time, this.camera, dogPoses);
      }
      for (const family of this.duckFamilies) {
        family.update(dtSec, time, this.camera, dogPoses);
      }
      for (const npc of this.parkNpcs) {
        npc.update(dtSec, time, this.camera, this.focus.x, this.focus.z);
      }
      for (const walker of this.leashWalkers) {
        walker.update(dtSec, time, this.camera, this.focus.x, this.focus.z);
      }
      for (const match of this.tennisMatches) {
        match.update(dtSec, time, this.camera);
      }
      for (const match of this.basketballMatches) {
        match.update(dtSec, time, this.camera);
      }
      for (const circle of this.drumCircles) {
        circle.update(dtSec, time, this.camera);
      }
      for (const spot of this.fishingSpots) {
        spot.update(dtSec, time, this.camera);
      }
      for (const game of this.frisbeeGames) {
        game.update(dtSec, time, this.camera, game.dogIsClose(this.dogs.values()));
      }
      for (const picnic of this.picnics) {
        picnic.update(time, this.camera);
      }
      const movers = this.collectMovers(dogPoses);
      const localDog = this.dogs.get(this.localId);
      let sprayHitLocal = false;
      for (const sprayer of this.lawnSprayers) {
        const triggered = sprayer.update(dtSec, movers);
        if (
          triggered &&
          localDog &&
          !this.previewMode &&
          sprayer.isNear(localDog.poseX, localDog.poseY)
        ) {
          sprayHitLocal = true;
        }
      }
      if (sprayHitLocal) {
        this.trySendReact("heart");
      }
      for (const dog of this.dogs.values()) {
        let soaking = false;
        for (const sprayer of this.lawnSprayers) {
          if (sprayer.isSoaking(dog.poseX, dog.poseY)) {
            soaking = true;
            break;
          }
        }
        dog.tickWetness(soaking, dtSec);
      }
      this.horizon?.update(dtSec);
      const local = this.dogs.get(this.localId);
      const localWx = local ? local.poseX + 0.5 : this.focus.x;
      const localWz = local ? local.poseY + 0.5 : this.focus.z;
      this.tickFlowerStir(dtSec, local, localWx, localWz);
      this.ambientVfx?.update(dtSec, localWx, localWz, this.camera.position.x, this.camera.position.z);
      this.updatePortalFx(time);
      this.updateWater(time);
      this.updateTeleportBursts(time);
      this.updateHarvestBursts(time);
      this.updateSwayProps(time);
      this.vegetation?.update(time);
      this.updateBenchChatters(time);
      this.updateSocialReactors(time);
      this.stageShow?.update(time, this.focus.x, this.focus.z);
      this.updateWorldLabels(this.focus.x, this.focus.z);
      this.updateInteractHighlights(time);
      this.tickLocalEnergy(dtSec);
      this.hud.tickAction(this.previewMode ? null : this.you?.action);
      this.renderer.render(this.scene, this.camera);
      this.labels.render(this.scene, this.camera);
      this.tuneQuality(delta);
      const info = this.renderer.info;
      this.perf.tick(delta, {
        dogs: this.dogs.size,
        critters: this.critters.length + this.duckFamilies.reduce((n, f) => n + f.count, 0),
        drawCalls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
        localX: local?.poseX ?? this.you?.x ?? 0,
        localY: local?.poseY ?? this.you?.y ?? 0,
        zone: this.map.zoneId,
      });
      this.drawMinimap(local);
    }
    this.frame = requestAnimationFrame(this.loop);
  };

  private drawMinimap(local: DogActor | undefined): void {
    if (this.previewMode) {
      return;
    }
    const lx = local?.poseX ?? this.you?.x ?? this.focus.x;
    const ly = local?.poseY ?? this.you?.y ?? this.focus.z;
    const dots: { x: number; y: number; local?: boolean; kind?: "player" | "npc"; color?: number }[] = [];

    // Other players (and local, skipped when drawing - arrow is used instead).
    for (const dog of this.dogs.values()) {
      dots.push({
        x: dog.poseX,
        y: dog.poseY,
        local: dog.playerId === this.localId,
        kind: "player",
        color: dog.state.color,
      });
    }

    // Wandering flavor NPCs - live positions.
    for (const npc of this.parkNpcs) {
      dots.push({ x: npc.poseX, y: npc.poseY, kind: "npc" });
    }
    for (const walker of this.leashWalkers) {
      dots.push({ x: walker.poseX, y: walker.poseY, kind: "npc" });
    }

    // Stationary NPCs, vendors, and bench humans.
    for (const node of this.nodes) {
      if (node.kind === "npc") {
        if (npcIsWanderer(node.npcKind)) {
          continue;
        }
        dots.push({ x: node.x, y: node.y, kind: "npc" });
      } else if (node.kind === "begSpot") {
        if (node.id.startsWith("amp-crowd-")) {
          continue;
        }
        dots.push({ x: node.x, y: node.y, kind: "npc" });
      }
    }

    this.minimap.draw(lx, ly, this.yaw, dots, this.walkTarget);
  }

  /** Drop pixel ratio when frames slip past ~60fps budget; recover slowly. */
  private tuneQuality(frameMs: number): void {
    this.frameMsEma = this.frameMsEma * 0.9 + frameMs * 0.1;
    this.qualityCooldown = Math.max(0, this.qualityCooldown - frameMs);
    if (this.qualityCooldown > 0) {
      return;
    }
    const current = this.renderer.getPixelRatio();
    if (this.frameMsEma > 18.5 && current > 1) {
      const next = Math.max(1, Math.round((current - 0.25) * 100) / 100);
      this.renderer.setPixelRatio(next);
      this.resize();
      this.qualityCooldown = 2000;
      this.ambientVfx?.setLowQuality(true);
    } else if (this.frameMsEma < 15.2 && current < this.pixelRatioCap) {
      const next = Math.min(this.pixelRatioCap, Math.round((current + 0.15) * 100) / 100);
      this.renderer.setPixelRatio(next);
      this.resize();
      this.qualityCooldown = 3500;
      this.ambientVfx?.setLowQuality(false);
    } else if (this.frameMsEma > 19) {
      this.ambientVfx?.setLowQuality(true);
    } else if (this.frameMsEma < 16) {
      this.ambientVfx?.setLowQuality(false);
    }
  }

  private readonly _labelWorld = new THREE.Vector3();
  private readonly _labelView = new THREE.Vector3();

  private updateWorldLabels(focusX: number, focusZ: number): void {
    for (const entry of this.worldLabels) {
      const el = entry.object.element as HTMLElement;
      entry.object.getWorldPosition(this._labelWorld);
      // Camera looks down −Z; anything with view.z ≳ 0 is behind / beside the lens.
      // CSS2D still draws those and pins them to screen edges (ghost shop badges).
      this._labelView.copy(this._labelWorld).applyMatrix4(this.camera.matrixWorldInverse);
      const behind = this._labelView.z > -0.5;
      this._labelView.copy(this._labelWorld).project(this.camera);
      const offscreen =
        behind ||
        this._labelView.z > 1 ||
        this._labelView.x < -1.05 ||
        this._labelView.x > 1.05 ||
        this._labelView.y < -1.1 ||
        this._labelView.y > 1.1;
      if (offscreen) {
        entry.object.visible = false;
        el.hidden = true;
        continue;
      }
      entry.object.visible = true;

      const dist = Math.hypot(this._labelWorld.x - focusX, this._labelWorld.z - focusZ);
      if (entry.kind === "node") {
        applyLabelLod(el, dist, { near: NODE_LABEL_NEAR, far: NODE_LABEL_FAR });
      } else if (entry.kind === "npc") {
        applyLabelLod(el, dist, { near: NPC_LABEL_NEAR, far: NPC_LABEL_FAR });
      } else if (entry.kind === "ico") {
        applyLabelLod(el, dist, {
          near: SHOP_ICO_NEAR,
          far: SHOP_ICO_FAR,
          preserveTransform: true,
        });
        if (!el.hidden && el.classList.contains("is-depleted")) {
          const base = Number(el.style.opacity || "1");
          el.style.opacity = String(base * 0.35);
        }
      } else {
        applyLabelLod(el, dist, { near: PAD_LABEL_NEAR, far: PAD_LABEL_FAR });
      }
      // CSS2DRenderer keys off Object3D.visible, not the DOM hidden attribute.
      entry.object.visible = !el.hidden;
    }
  }

  private updateBillboards(time: number): void {
    const local = this.dogs.get(this.localId);
    const dogX = local ? local.poseX + 0.5 : null;
    const dogZ = local ? local.poseY + 0.5 : null;

    for (const billboard of this.billboards) {
      const hop =
        billboard.bob &&
        !(
          dogX != null &&
          dogZ != null &&
          Math.hypot(billboard.worldX - dogX, billboard.worldZ - dogZ) < 3.8
        )
          ? Math.max(0, Math.sin(time * 0.01 + billboard.phase) * 0.09)
          : 0;
      const y = billboard.baseY + hop;
      let px = billboard.worldX;
      let pz = billboard.worldZ;
      if (billboard.cameraDepth) {
        const dx = this.camera.position.x - billboard.worldX;
        const dz = this.camera.position.z - billboard.worldZ;
        const len = Math.hypot(dx, dz);
        if (len > 1e-5) {
          px += (dx / len) * billboard.cameraDepth;
          pz += (dz / len) * billboard.cameraDepth;
        }
      }
      billboard.mesh.position.set(px, y, pz);
      if (billboard.highlight && billboard.cameraDepth) {
        billboard.highlight.group.position.x = px;
        billboard.highlight.group.position.z = pz;
      }
      faceCameraYaw(billboard.mesh, this.camera);
      const flip = facingFlipSign(billboard.worldX, billboard.worldZ, billboard.facing, this.camera);
      if (billboard.bob) {
        // Soft idle pulse on scale - never tip the billboard (that caused flips).
        const pulse = 1 + Math.sin(time * 0.0048 + billboard.phase) * 0.048;
        billboard.mesh.scale.set(flip * pulse, pulse, 1);
        if (billboard.rim) {
          placeRimBehind(billboard.rim, billboard.mesh, flip * pulse, pulse);
        }
      } else {
        billboard.mesh.scale.set(flip, 1, 1);
        if (billboard.rim) {
          placeRimBehind(billboard.rim, billboard.mesh, flip, 1);
        }
      }
      if (billboard.npcKind) {
        const idleFrame = Math.floor(time / 140 + billboard.phase) % 10;
        if (idleFrame !== billboard.frame) {
          billboard.frame = idleFrame;
          const map = this.art.npcIdle(billboard.npcKind, idleFrame);
          const material = billboard.mesh.material as THREE.MeshBasicMaterial;
          material.map = map;
          material.needsUpdate = true;
          if (billboard.rim) {
            setSilhouetteMap(billboard.rim, map);
          }
          billboard.highlight?.setMap(map);
        }
      }
    }
  }

  private updateInteractHighlights(time: number): void {
    for (const target of this.interactables) {
      target.highlight.update(time, this.camera);
    }
  }

  private groundY = (poseX: number, poseY: number): number => {
    const wx = poseX + 0.5;
    const wz = poseY + 0.5;
    return this.amphAwareGround(Math.floor(wx), Math.floor(wz), wx, wz);
  };

  /** Include amphitheater seating-bowl lift when standing on raised Floor tiers. */
  private amphAwareGround(tileX: number, tileY: number, worldX: number, worldZ: number): number {
    let hy = terrainHeightAt(this.map, worldX, worldZ);
    const stage = this.map.scenery?.find((s) => s.kind === "stage");
    if (stage) {
      // Match the raised Floor mesh (discrete per tile) so paws sit on the bowl.
      hy += amphFloorLift(tileX, tileY, stage.x, stage.y);
    }
    for (const surface of this.walkSurfaces) {
      const dx = worldX - surface.x;
      const dz = worldZ - surface.z;
      if (dx * dx + dz * dz <= surface.radius * surface.radius) {
        hy = Math.max(hy, surface.surfaceY);
      }
    }
    return hy;
  }

  /** World facing (radians) toward the amphitheater stage, if present. */
  private facingTowardStage(worldX: number, worldZ: number): number | null {
    const stage = this.map.scenery?.find((s) => s.kind === "stage");
    if (!stage) {
      return null;
    }
    return Math.atan2(stage.y + 0.5 - worldZ, stage.x + 0.5 - worldX);
  }

  private updateClick(delta: number): void {
    if (!this.clickMarker.visible) {
      return;
    }
    const material = this.clickMarker.material as THREE.MeshBasicMaterial;
    if (this.walkTarget?.mode === "flag") {
      return;
    }
    this.clickFade = Math.max(0, this.clickFade - delta / 900);
    material.opacity = this.clickFade;
    if (this.clickFade <= 0.02) {
      this.clickMarker.visible = false;
    }
  }

  private updateWaypoint(time: number): void {
    if (!this.walkTarget || this.previewMode) {
      this.waypointFlag.visible = false;
      this.travelRing.visible = false;
      this.harvestRing.visible = false;
      return;
    }
    const local = this.dogs.get(this.localId);
    if (!local) {
      this.waypointFlag.visible = false;
      this.travelRing.visible = false;
      this.harvestRing.visible = false;
      return;
    }
    const dist = Math.hypot(local.poseX - this.walkTarget.x, local.poseY - this.walkTarget.y);
    const arriveR = this.walkTarget.mode === "travel" ? Math.max(0.55, (this.walkTarget.radius ?? 1.2) * 0.35) : 0.35;
    if (dist < arriveR) {
      this.clearWaypoint();
      if (this.selectedInteractableId?.startsWith("pad:") || this.selectedInteractableId?.startsWith("node:")) {
        this.setSelectedInteractable(null);
      }
      return;
    }

    if (this.walkTarget.mode === "travel") {
      this.waypointFlag.visible = false;
      this.harvestRing.visible = false;
      this.clickMarker.visible = false;
      this.updateTravelRing(time);
      return;
    }

    if (this.walkTarget.mode === "harvest") {
      this.waypointFlag.visible = false;
      this.travelRing.visible = false;
      this.clickMarker.visible = false;
      this.updateHarvestRing(time);
      return;
    }

    this.travelRing.visible = false;
    this.harvestRing.visible = false;
    this.clickMarker.visible = false;
    const wx = this.walkTarget.x + 0.5;
    const wz = this.walkTarget.y + 0.5;
    const hy = this.walkTarget.surfaceY;
    this.waypointFlag.visible = true;
    this.waypointFlag.position.set(wx, hy, wz);
    // Yaw-only so the sleeve stays vertical on the pole (full billboard tips it off).
    faceCameraHorizontal(this.waypointHoist, this.camera);
    this.waveWaypointFlag(time);
  }

  /** Pulse the teleporter destination ring while the pup is en route. */
  private updateTravelRing(time: number): void {
    if (!this.walkTarget || this.walkTarget.mode !== "travel") {
      this.travelRing.visible = false;
      return;
    }
    const cx = this.walkTarget.x;
    const cz = this.walkTarget.y;
    const hy = this.walkTarget.surfaceY;
    const baseR = Math.max(1.25, this.walkTarget.radius ?? 1.5);
    const t = time * 0.001;
    // Opacity breathe (~0.9s per cycle).
    const pulse = 0.5 + 0.5 * Math.sin(t * 7);
    this.travelRing.visible = true;
    this.travelRing.position.set(cx, hy + 0.08, cz);
    this.travelRing.scale.setScalar(baseR);

    const outerMat = this.travelRingOuter.material as THREE.MeshBasicMaterial;
    outerMat.opacity = 0.28 + pulse * 0.5;
    this.travelRingOuter.rotation.z = -t * 0.18;
  }

  /** Pulse the harvestable destination ring while the pup is en route. */
  private updateHarvestRing(time: number): void {
    if (!this.walkTarget || this.walkTarget.mode !== "harvest") {
      this.harvestRing.visible = false;
      return;
    }
    const cx = this.walkTarget.ringX ?? this.walkTarget.x;
    const cz = this.walkTarget.ringZ ?? this.walkTarget.y;
    const hy = this.walkTarget.ringSurfaceY ?? this.walkTarget.surfaceY;
    const baseR = Math.max(0.85, this.walkTarget.radius ?? 0.95);
    const t = time * 0.001;
    const pulse = 0.5 + 0.5 * Math.sin(t * 7);
    this.harvestRing.visible = true;
    this.harvestRing.position.set(cx, hy + 0.08, cz);
    this.harvestRing.scale.setScalar(baseR);

    const outerMat = this.harvestRingOuter.material as THREE.MeshBasicMaterial;
    outerMat.opacity = 0.28 + pulse * 0.5;
    this.harvestRingOuter.rotation.z = -t * 0.18;
  }

  /** Soft wind ripple on the free edge; hoist column stays pinned to the pole. */
  private waveWaypointFlag(time: number): void {
    const pos = this.waypointBanner.geometry.attributes.position;
    if (!pos) {
      return;
    }
    const rest = this.waypointBannerRest;
    const flagW = 1.05;
    for (let i = 0; i < pos.count; i++) {
      const ix = i * 3;
      const x = rest[ix]!;
      const y = rest[ix + 1]!;
      const u = Math.max(0, Math.min(1, x / flagW));
      // Keep the sleeve column flush; only the free cloth waves.
      const flap = u < 0.1 ? 0 : ((u - 0.1) / 0.9) ** 1.15;
      const ripple =
        Math.sin(time * 0.014 + u * 5.1 + y * 7.2) * flap * 0.11 +
        Math.sin(time * 0.023 + u * 8.6) * flap * flap * 0.075 +
        Math.sin(time * 0.031 + u * 3.8 + y * 5.4) * flap * 0.045 +
        Math.sin(time * 0.048 + u * 11.2 + y * 2.1) * flap * flap * 0.028;
      const sag = Math.sin(time * 0.011 + u * 3.2) * flap * 0.048;
      pos.setXYZ(i, x, y + sag, ripple);
    }
    pos.needsUpdate = true;
    this.waypointBanner.geometry.computeVertexNormals();
  }

  private setWaypoint(poseX: number, poseY: number, surfaceY?: number): void {
    const hy = surfaceY ?? this.groundY(poseX, poseY);
    this.walkTarget = { x: poseX, y: poseY, surfaceY: hy, mode: "flag" };
    const wx = poseX + 0.5;
    const wz = poseY + 0.5;
    this.travelRing.visible = false;
    this.harvestRing.visible = false;
    this.clickMarker.visible = false;
    this.clickFade = 0;
    this.waypointFlag.position.set(wx, hy, wz);
    this.waypointFlag.visible = true;
  }

  /** Walk-to-teleporter marker: glowing pad ring, no bone flag. */
  private setTravelWaypoint(poseX: number, poseY: number, radius: number): void {
    const surfaceY = this.surfaceYAt(poseX, poseY);
    this.walkTarget = { x: poseX, y: poseY, surfaceY, mode: "travel", radius };
    this.waypointFlag.visible = false;
    this.clickMarker.visible = false;
    this.clickFade = 0;
    this.updateTravelRing(performance.now());
  }

  /** Walk-to-harvestable marker: yellow glow ring at the node, no bone flag. */
  private setHarvestWaypoint(
    walkX: number,
    walkY: number,
    ringX: number,
    ringZ: number,
    radius = 0.95,
  ): void {
    const surfaceY = this.surfaceYAt(ringX, ringZ);
    this.walkTarget = {
      x: walkX,
      y: walkY,
      surfaceY: this.surfaceYAt(walkX, walkY),
      mode: "harvest",
      radius,
      ringX,
      ringZ,
      ringSurfaceY: surfaceY,
    };
    this.waypointFlag.visible = false;
    this.travelRing.visible = false;
    this.clickMarker.visible = false;
    this.clickFade = 0;
    this.updateHarvestRing(performance.now());
  }

  private surfaceYAt(wx: number, wz: number): number {
    const hy = this.amphAwareGround(Math.floor(wx), Math.floor(wz), wx, wz);
    let surfaceY = hy;
    for (const surface of this.walkSurfaces) {
      const dx = wx - surface.x;
      const dz = wz - surface.z;
      if (dx * dx + dz * dz <= surface.radius * surface.radius) {
        surfaceY = Math.max(surfaceY, surface.surfaceY);
      }
    }
    return surfaceY;
  }

  private approachHarvestNode(node: ResourceNodeDef): void {
    const range = interactRangeForNode(node);
    const dest = nearestInteractPoint(this.map, this.localPose(), { x: node.x, y: node.y }, range);
    if (dest) {
      const approach = buildContinuousPath(this.map, this.localPose(), dest);
      if (approach.length > 0) {
        this.dogs.get(this.localId)?.predictPath(approach);
      }
      this.setHarvestWaypoint(dest.x, dest.y, node.x, node.y);
    } else {
      this.setHarvestWaypoint(node.x, node.y, node.x, node.y);
    }
    this.net.send({ type: "interact", nodeId: node.id });
    this.lastHarvestNodeId = node.id;
  }

  private clearWaypoint(): void {
    this.walkTarget = null;
    this.waypointFlag.visible = false;
    this.travelRing.visible = false;
    this.harvestRing.visible = false;
    this.clickMarker.visible = false;
    this.clickFade = 0;
  }

  private updateCamera(delta: number): void {
    if (this.previewMode) {
      this.yaw += delta * 0.00004;
    } else {
      const local = this.dogs.get(this.localId);
      if (local) {
        const targetX = local.poseX + 0.5;
        const targetZ = local.poseY + 0.5;
        const targetY = this.groundY(local.poseX, local.poseY) + 0.2;
        // Always soft-follow - never snap when orbit starts (that caused jitter).
        const follow = this.orbiting ? Math.min(1, CAMERA_LERP * 3.2) : CAMERA_LERP;
        const t = 1 - (1 - follow) ** (delta / 16.67);
        this.focus.x += (targetX - this.focus.x) * t;
        this.focus.z += (targetZ - this.focus.z) * t;
        this.focus.y += (targetY - this.focus.y) * t;
      }
    }
    this.focus.x = clamp(this.focus.x, 1, this.map.width - 1);
    this.focus.z = clamp(this.focus.z, 1, this.map.height - 1);
    const cosPitch = Math.cos(this.pitch);
    this.camera.position.set(
      this.focus.x + Math.sin(this.yaw) * cosPitch * this.distance,
      this.focus.y + Math.sin(this.pitch) * this.distance,
      this.focus.z + Math.cos(this.yaw) * cosPitch * this.distance,
    );
    this.camera.lookAt(this.focus);
  }

  private snapFocusToLocal(): void {
    const local = this.dogs.get(this.localId);
    const x = local?.poseX ?? this.you?.x ?? this.previewFocus.x;
    const y = local?.poseY ?? this.you?.y ?? this.previewFocus.y;
    this.focus.set(x + 0.5, this.groundY(x, y) + 0.2, y + 0.5);
  }

  /** Canonical park framing on login (ignores title-screen orbit drift). */
  private resetLoginCamera(): void {
    this.yaw = CAMERA_YAW;
    this.pitch = CAMERA_PITCH;
    this.distance = CAMERA_DISTANCE;
    this.orbiting = false;
    this.spaceDown = false;
    this.snapFocusToLocal();
  }

  private onMessage(message: ServerMessage): void {
    switch (message.type) {
      case "spectatorWelcome":
        this.applySpectator(message);
        break;
      case "welcome":
        this.enterPlay(message);
        break;
      case "zoneChange":
        this.applyZone(message);
        break;
      case "state":
        if (message.you) {
          this.you = message.you;
          if (typeof message.you.energy === "number") {
            this.localEnergy = message.you.energy;
            if (this.localEnergy <= 0 && this.isRunning()) {
              this.exhaustRun();
            }
          }
          if (!this.previewMode) {
            this.hud.render(message.you);
          }
          this.syncRun();
        }
        if (message.nodeRuntime) {
          this.applyNodeRuntime(message.nodeRuntime);
        }
        for (const player of message.players) {
          this.upsertDog(player);
        }
        this.pruneDogs(message.players);
        break;
      case "playerJoined":
        this.upsertDog(message.player);
        if (!this.previewMode) {
          this.hud.toast(`${message.player.name} trotted into the park.`);
        }
        break;
      case "playerLeft":
        if (this.hoveredDogId === message.playerId) {
          this.setHoveredDog(null);
        }
        this.dogs.get(message.playerId)?.destroy();
        this.dogs.delete(message.playerId);
        break;
      case "teleportFx":
        this.playTeleportFx(message);
        break;
      case "xp": {
        if (this.previewMode) {
          break;
        }
        const dog = this.dogs.get(this.localId);
        const skill = SKILLS[message.skillId];
        const accent = skillColorCss(message.skillId);
        dog?.floatText(`+${message.amount} ${skill.verb}`, accent);
        if (message.leveledUp) {
          dog?.floatText(`${skill.name} ${message.level}!`, accent);
        }
        if (message.item) {
          dog?.floatText(`+${message.item.quantity} ${message.item.itemId}`, accent);
          this.playHarvestBurst(message.skillId);
        }
        this.hud.showXpGain(message);
        break;
      }
      case "toast":
        if (!this.previewMode) {
          this.hud.toast(message.message);
        }
        break;
      case "bark":
        if (!this.previewMode) {
          this.hud.pushBark(message.name, message.text);
        }
        break;
      case "react":
        this.dogs.get(message.playerId)?.showReaction(message.reactionId);
        break;
      case "error":
        if (!this.previewMode) {
          this.hud.toast(message.message);
        }
        break;
      default:
        break;
    }
  }

  /** Ask the server to broadcast a reaction (heart, etc.) above this pup. */
  private trySendReact(reactionId: "heart"): void {
    if (this.previewMode) {
      return;
    }
    const now = performance.now();
    if (now - this.lastReactSentAt < REACT_COOLDOWN_MS) {
      return;
    }
    this.lastReactSentAt = now;
    this.net.send({ type: "react", reactionId });
  }

  private pruneDogs(players: PlayerPublicState[]): void {
    for (const [id, dog] of this.dogs) {
      if (!players.some((player) => player.id === id)) {
        dog.destroy();
        this.dogs.delete(id);
      }
    }
  }

  private upsertDog(state: PlayerPublicState): void {
    const existing = this.dogs.get(state.id);
    if (existing) {
      existing.applyState(state, performance.now());
      return;
    }
    if (!this.ready) {
      const index = this.queuePlayers.findIndex((player) => player.id === state.id);
      if (index >= 0) {
        this.queuePlayers[index] = state;
      } else {
        this.queuePlayers.push(state);
      }
      return;
    }
    const actor = new DogActor(this.art, state, state.id === this.localId, this.groundY);
    if (state.id === this.localId) {
      actor.setRunning(this.isRunning() && this.localEnergy > 0);
    }
    this.dogs.set(state.id, actor);
    this.scene.add(actor.group);
  }

  private replaceMap(map: MapData, nodes: ResourceNodeDef[]): void {
    this.map = bakeMapCollision(map);
    this.nodes = nodes;
    this.minimap.setMap(this.map);
    if (this.ready) {
      this.buildWorld();
    }
  }

  private buildWorld(): void {
    this.clearWorld();
    this.drawGround();
    this.drawHorizon();
    this.drawPierDecks();
    this.drawProps();
    this.drawScenery();
    this.placeNodes();
    this.drawTeleporters();
    this.drawMarkers();
    this.spawnCritters();
    this.spawnTennisMatches();
    this.spawnBasketballMatches();
    this.spawnDrumCircles();
    this.spawnFishingSpots();
    this.spawnFrisbeeGames();
    this.spawnPicnics();
    this.spawnLawnSprayers();
    this.spawnAmbientVfx();
  }

  private clearWorld(): void {
    this.clearCritters();
    this.horizon?.destroy();
    this.horizon = null;
    this.ambientVfx?.destroy();
    this.ambientVfx = null;
    this.vegetation?.destroy();
    this.vegetation = null;
    this.portalFx.length = 0;
    this.waterMesh = null;
    this.waterBasePositions = null;
    this.walkSurfaces.length = 0;
    for (const burst of this.teleportBursts) {
      burst.removeFromParent();
    }
    this.teleportBursts.length = 0;
    for (const burst of this.harvestBursts) {
      burst.removeFromParent();
    }
    this.harvestBursts.length = 0;
    this.lastHarvestNodeId = null;
    this.swayProps.length = 0;
    for (const entry of this.interactables) {
      // Wandering NPC highlights are owned by ParkNpc.destroy().
      if (!entry.id.startsWith("talk:")) {
        entry.highlight.destroy();
      }
    }
    this.interactables.length = 0;
    this.gatherVisuals.clear();
    this.depletedNodes.clear();
    this.benchChatters.length = 0;
    this.socialReactors.length = 0;
    this.stageShow?.destroy();
    this.stageShow = null;
    this.worldRoot.traverse((child) => {
      if (child instanceof CSS2DObject) {
        child.element.remove();
      }
      if (child instanceof THREE.Points) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
      if (child instanceof THREE.Mesh || child instanceof THREE.InstancedMesh) {
        if (!child.geometry.userData.shared) {
          child.geometry.dispose();
        }
        const material = child.material;
        if (Array.isArray(material)) {
          for (const item of material) {
            if (!item.userData.shared) {
              item.dispose();
            }
          }
        } else if (material && !material.userData.shared && material !== this.clickMarker.material) {
          material.dispose();
        }
      }
    });
    this.worldRoot.clear();
    this.nodeByTile.clear();
    this.billboards.length = 0;
    this.worldLabels.length = 0;
    this.hoveredInteractableId = null;
  }

  private drawHorizon(): void {
    this.horizon = new HorizonDecor(this.map);
    this.worldRoot.add(this.horizon.root);
  }

  private spawnAmbientVfx(): void {
    this.ambientVfx?.destroy();
    this.ambientVfx = new AmbientVfx(this.map.width, this.map.height);
    this.worldRoot.add(this.ambientVfx.root);
  }

  private updatePortalFx(time: number): void {
    if (this.portalFx.length === 0) {
      return;
    }
    const t = time * 0.001;
    for (const obj of this.portalFx) {
      const kind = (obj.userData.portalPulse ?? obj.userData.ambientGlow) as string | undefined;
      if (!kind) {
        continue;
      }
      if (
        (kind === "motes" || kind === "motesOuter" || kind === "motesSpark" || kind === "fountainSpray") &&
        obj instanceof THREE.Points
      ) {
        if (kind === "fountainSpray") {
          const mat = obj.material;
          if (mat instanceof THREE.PointsMaterial) {
            mat.opacity = 0.55 + Math.sin(t * 4.2 + obj.id) * 0.3;
            mat.size = 0.07 + Math.sin(t * 5.1 + obj.id) * 0.035;
          }
          obj.position.y = Math.sin(t * 3.6 + obj.id) * 0.04;
          continue;
        }
        const anim = obj.userData.moteAnim as
          | {
              rimR: number;
              topY: number;
              phases: Float32Array;
              spin: number;
              riseH: number;
              riseSpeed: number;
              bob: number;
              linger?: number;
            }
          | undefined;
        const mat = obj.material;
        if (anim) {
          const pos = obj.geometry.getAttribute("position") as THREE.BufferAttribute;
          const count = pos.count;
          const linger = anim.linger ?? 0.35;
          for (let i = 0; i < count; i++) {
            const phase = anim.phases[i] ?? (i / count) * Math.PI * 2;
            const riseU = ((t * anim.riseSpeed + phase * 0.16) % 1 + 1) % 1;
            // Linger near the rim so the circle stays continuous, then rise.
            let lift: number;
            if (riseU < linger) {
              lift = (riseU / linger) * 0.06;
            } else {
              const u = (riseU - linger) / Math.max(0.001, 1 - linger);
              lift = 0.06 + u * u * anim.riseH;
            }
            const swirl =
              t * anim.spin +
              (i / count) * Math.PI * 2 +
              Math.sin(t * 1.35 + phase) * 0.1 +
              Math.sin(t * 0.55 + phase * 0.5) * 0.06;
            const radial =
              anim.rimR *
              (1 +
                Math.sin(t * 1.8 + phase) * 0.028 +
                (kind === "motesSpark" ? lift * 0.07 : lift * 0.025));
            const y =
              anim.topY +
              0.04 +
              lift +
              Math.sin(t * 2.8 + phase * 2.1) * anim.bob;
            pos.setXYZ(i, Math.cos(swirl) * radial, y, Math.sin(swirl) * radial);
          }
          pos.needsUpdate = true;
          if (mat instanceof THREE.PointsMaterial) {
            // Soft glow only — never fully opaque (cap 70%).
            if (kind === "motesOuter") {
              mat.opacity = 0.28 + Math.sin(t * 1.7) * 0.14;
              mat.size = 0.15 + Math.sin(t * 2.2) * 0.035;
            } else if (kind === "motesSpark") {
              mat.opacity = 0.36 + Math.sin(t * 3.8) * 0.2;
              mat.size = 0.09 + Math.sin(t * 4.6) * 0.035;
            } else {
              mat.opacity = 0.42 + Math.sin(t * 2.1) * 0.14;
              mat.size = 0.24 + Math.sin(t * 2.4) * 0.045;
            }
          }
        } else {
          // Legacy portals without moteAnim still spin.
          obj.rotation.y = kind === "motesOuter" ? -t * 0.35 : t * 0.55;
        }
        continue;
      }
      if (!(obj instanceof THREE.Mesh)) {
        continue;
      }
      const mat = obj.material;
      if (!(mat instanceof THREE.MeshBasicMaterial)) {
        continue;
      }
      if (kind === "glow") {
        // Textured disc - pulse tint only (never scale; no second overlay).
        const pulse = 0.88 + Math.sin(t * 2.2) * 0.12;
        mat.color.setRGB(pulse * 0.92, pulse, pulse * 1.02);
      } else if (kind === "halo") {
        mat.opacity = 0.2 + Math.sin(t * 1.9) * 0.1;
        const breathe = 1 + Math.sin(t * 1.6) * 0.04;
        obj.scale.set(breathe, breathe, 1);
      } else if (kind === "ring") {
        obj.rotation.z = t * 0.65;
        const pulse = 0.82 + Math.sin(t * 3.2) * 0.14;
        mat.color.setRGB(pulse * 0.92, pulse, 1.05);
        const breathe = 1 + Math.sin(t * 2.1) * 0.035;
        obj.scale.set(breathe, breathe, 1);
      } else if (kind === "fountainWater") {
        mat.opacity = 0.58 + Math.sin(t * 2.8 + obj.position.x) * 0.14;
      } else if (kind === "lamp") {
        const pulse = 1 + Math.sin(t * 2.4 + obj.position.x) * 0.08;
        if (mat.transparent) {
          mat.opacity = 0.22 + Math.sin(t * 2.4 + obj.position.x) * 0.1;
        }
        obj.scale.setScalar(pulse);
      }
    }
  }

  private playTeleportFx(message: Extract<ServerMessage, { type: "teleportFx" }>): void {
    const burst = makeTeleportBurst(message.phase);
    burst.position.set(message.x + 0.5, this.groundY(message.x, message.y), message.y + 0.5);
    this.worldRoot.add(burst);
    this.teleportBursts.push(burst);

    const dog = this.dogs.get(message.playerId);
    const isLocal = message.playerId === this.localId;

    if (message.phase === "arrive") {
      dog?.snapPose(message.x, message.y);
      if (isLocal) {
        if (this.you) {
          this.you.x = message.x;
          this.you.y = message.y;
        }
        this.clearWaypoint();
        this.snapFocusToLocal();
        if (!this.previewMode) {
          this.hud.endTeleportCover(message.label);
        }
      }
    } else if (message.phase === "leave" && isLocal) {
      this.clearWaypoint();
      dog?.snapPose(message.x, message.y);
      if (!this.previewMode) {
        this.hud.beginTeleportCover();
      }
    }
  }

  private updateTeleportBursts(time: number): void {
    if (this.teleportBursts.length === 0) {
      return;
    }
    const remaining: THREE.Group[] = [];
    for (const burst of this.teleportBursts) {
      const data = burst.userData.teleportBurst as
        | {
            started: number;
            duration: number;
            velocities: Float32Array;
            points: THREE.Points;
            ring: THREE.Mesh;
          }
        | undefined;
      if (!data) {
        burst.removeFromParent();
        continue;
      }
      const u = (time - data.started) / data.duration;
      if (u >= 1) {
        data.points.geometry.dispose();
        if (data.points.material instanceof THREE.Material) {
          data.points.material.dispose();
        }
        data.ring.geometry.dispose();
        if (data.ring.material instanceof THREE.Material) {
          data.ring.material.dispose();
        }
        burst.removeFromParent();
        continue;
      }
      const pos = data.points.geometry.getAttribute("position") as THREE.BufferAttribute;
      const dt = Math.min(0.05, (time - data.started) * 0.001);
      for (let i = 0; i < pos.count; i++) {
        const ix = i * 3;
        pos.setXYZ(
          i,
          pos.getX(i) + data.velocities[ix]! * dt,
          pos.getY(i) + data.velocities[ix + 1]! * dt,
          pos.getZ(i) + data.velocities[ix + 2]! * dt,
        );
        data.velocities[ix + 1]! -= 2.8 * dt;
      }
      pos.needsUpdate = true;
      const fade = 1 - u;
      if (data.points.material instanceof THREE.PointsMaterial) {
        data.points.material.opacity = 0.95 * fade;
        data.points.material.size = 0.18 * (0.7 + fade * 0.3);
      }
      if (data.ring.material instanceof THREE.MeshBasicMaterial) {
        data.ring.material.opacity = 0.7 * fade;
      }
      data.ring.scale.setScalar(1 + u * 1.6);
      remaining.push(burst);
    }
    this.teleportBursts.length = 0;
    this.teleportBursts.push(...remaining);
  }

  private playHarvestBurst(skillId: string): void {
    const nodeId = this.lastHarvestNodeId;
    const node = nodeId ? this.nodes.find((n) => n.id === nodeId) : undefined;
    const wx = node ? node.x + 0.5 : (this.dogs.get(this.localId)?.poseX ?? 0) + 0.5;
    const wz = node ? node.y + 0.5 : (this.dogs.get(this.localId)?.poseY ?? 0) + 0.5;
    const hy = this.groundY(wx - 0.5, wz - 0.5);
    const burst = makeHarvestBurst(skillId);
    burst.position.set(wx, hy, wz);
    this.worldRoot.add(burst);
    this.harvestBursts.push(burst);
  }

  private updateHarvestBursts(time: number): void {
    if (this.harvestBursts.length === 0) {
      return;
    }
    const remaining: THREE.Group[] = [];
    for (const burst of this.harvestBursts) {
      const data = burst.userData.harvestBurst as
        | {
            started: number;
            duration: number;
            velocities: Float32Array;
            points: THREE.Points;
            ring: THREE.Mesh;
          }
        | undefined;
      if (!data) {
        burst.removeFromParent();
        continue;
      }
      const u = (time - data.started) / data.duration;
      if (u >= 1) {
        data.points.geometry.dispose();
        if (data.points.material instanceof THREE.Material) {
          data.points.material.dispose();
        }
        data.ring.geometry.dispose();
        if (data.ring.material instanceof THREE.Material) {
          data.ring.material.dispose();
        }
        burst.removeFromParent();
        continue;
      }
      const pos = data.points.geometry.getAttribute("position") as THREE.BufferAttribute;
      const dt = Math.min(0.05, (time - data.started) * 0.001);
      for (let i = 0; i < pos.count; i++) {
        const ix = i * 3;
        pos.setXYZ(
          i,
          pos.getX(i) + data.velocities[ix]! * dt,
          pos.getY(i) + data.velocities[ix + 1]! * dt,
          pos.getZ(i) + data.velocities[ix + 2]! * dt,
        );
        data.velocities[ix + 1]! -= 3.2 * dt;
      }
      pos.needsUpdate = true;
      const fade = 1 - u * u;
      if (data.points.material instanceof THREE.PointsMaterial) {
        data.points.material.opacity = 0.95 * fade;
        data.points.material.size = 0.16 * (0.65 + fade * 0.45);
      }
      if (data.ring.material instanceof THREE.MeshBasicMaterial) {
        data.ring.material.opacity = 0.75 * fade;
      }
      data.ring.scale.setScalar(1 + u * 2.1);
      remaining.push(burst);
    }
    this.harvestBursts.length = 0;
    this.harvestBursts.push(...remaining);
  }

  private updateSwayProps(time: number): void {
    if (this.swayProps.length === 0) {
      return;
    }
    const t = time * 0.001;
    for (const entry of this.swayProps) {
      const amp = entry.soft ? 0.155 : 0.065;
      const wind = parkWindSample(entry.x, entry.z, t);
      const along = wind.along * amp;
      const cross = wind.cross * amp;
      entry.root.rotation.z = -along * PARK_WIND_DIR_X + cross * PARK_WIND_DIR_Z;
      entry.root.rotation.x = along * PARK_WIND_DIR_Z + cross * PARK_WIND_DIR_X;
      if (entry.soft) {
        const stretch = 1 + Math.max(0, wind.along) * 0.06 + Math.abs(wind.cross) * 0.02;
        entry.root.scale.set(
          entry.baseScale * (1 - Math.abs(along) * 0.12),
          entry.baseScale * stretch,
          entry.baseScale * (1 - Math.abs(along) * 0.12),
        );
      }
    }
  }

  private drawGround(): void {
    // Interior underlay only - horizon skirt covers the outer void.
    const under = new THREE.Mesh(
      new THREE.PlaneGeometry(this.map.width + 2, this.map.height + 2),
      new THREE.MeshLambertMaterial({ color: 0xa8d070 }),
    );
    under.rotation.x = -Math.PI / 2;
    under.position.set(this.map.width / 2, -0.55, this.map.height / 2);
    under.receiveShadow = false;
    this.worldRoot.add(under);

    // Gentle shared corner warps - enough to soften the grid, not enough to
    // shear textures into jagged triangles.
    const warpAmp = 0.14;
    const cornerX = (x: number, z: number): number => {
      if (x <= 0 || z <= 0 || x >= this.map.width || z >= this.map.height) {
        return x;
      }
      return x + (Math.sin(x * 1.71 + z * 2.33) * 0.5 + Math.sin(x * 0.55 - z * 1.1) * 0.5) * warpAmp;
    };
    const cornerZ = (x: number, z: number): number => {
      if (x <= 0 || z <= 0 || x >= this.map.width || z >= this.map.height) {
        return z;
      }
      return z + (Math.sin(x * 2.09 - z * 1.47) * 0.5 + Math.sin(x * 0.73 + z * 0.91) * 0.5) * warpAmp;
    };

    // Continuous quads (shared corner heights) - no overlapping planes → no z-fighting.
    // Path tiles stay walkable in map data but are hidden when curved ribbons exist;
    // lawn shows under the corridor so only the ribbon reads as sidewalk.
    const hidePathSquares = (this.map.pathRibbons?.length ?? 0) > 0;
    // Plank models cover the dock - skip opaque Pier quads so water shows in the gaps.
    const pierUsesPlanks = townTemplates("plank").some((t) => t.id === "planks");
    type GroundCell = { x: number; y: number; sx: number; sy: number; sw: number; sh: number };
    const groups = new Map<Tile, GroundCell[]>();

    const pushCell = (tile: Tile, cell: GroundCell): void => {
      const list = groups.get(tile) ?? [];
      list.push(cell);
      groups.set(tile, list);
    };

    const tileAt = (x: number, y: number): Tile => {
      const raw = this.map.tiles[y]?.[x] ?? Tile.Grass;
      return hidePathSquares && raw === Tile.Path ? Tile.Grass : raw;
    };

    for (let y = 0; y < this.map.height; y++) {
      for (let x = 0; x < this.map.width; x++) {
        const tile = tileAt(x, y);
        // Legacy CourtLine tiles (if any) render as asphalt under paint marks.
        if (tile === Tile.CourtLine) {
          pushCell(Tile.CourtBasket, { x, y, sx: 0, sy: 0, sw: 1, sh: 1 });
          continue;
        }
        if (tile === Tile.Pier) {
          // Keep pond water under every dock slab (map Pier replaces Water).
          pushCell(Tile.Water, { x, y, sx: 0, sy: 0, sw: 1, sh: 1 });
          if (!pierUsesPlanks) {
            pushCell(Tile.Pier, { x, y, sx: 0, sy: 0, sw: 1, sh: 1 });
          }
          continue;
        }
        pushCell(tile, { x, y, sx: 0, sy: 0, sw: 1, sh: 1 });
      }
    }

    const stageScenery = this.map.scenery?.find((s) => s.kind === "stage");
    // Flat pond surface - never pull pier/lawn heights into water corners.
    const waterSurfaceY = (wx: number, wz: number): number =>
      -0.22 +
      (Math.sin(wx * 0.19 + wz * 0.14) * 0.35 + Math.sin(wx * 0.07 - wz * 0.17) * 0.28) * 0.015;

    for (const [tile, cells] of groups) {
      const positions: number[] = [];
      const uvs: number[] = [];
      const indices: number[] = [];
      let vertex = 0;
      for (const cell of cells) {
        const paved =
          tile === Tile.Floor ||
          tile === Tile.Pier ||
          tile === Tile.Path ||
          tile === Tile.CourtBasket ||
          tile === Tile.CourtTennis ||
          tile === Tile.CourtLine;
        const softEdge =
          tile === Tile.Floor &&
          (isLawnish(this.map.tiles[cell.y - 1]?.[cell.x]) ||
            isLawnish(this.map.tiles[cell.y + 1]?.[cell.x]) ||
            isLawnish(this.map.tiles[cell.y]?.[cell.x - 1]) ||
            isLawnish(this.map.tiles[cell.y]?.[cell.x + 1]));
        // Don't warp shore lawn into the pond (reads as floating grass chips).
        const besideWater =
          !paved &&
          (this.map.tiles[cell.y - 1]?.[cell.x] === Tile.Water ||
            this.map.tiles[cell.y + 1]?.[cell.x] === Tile.Water ||
            this.map.tiles[cell.y]?.[cell.x - 1] === Tile.Water ||
            this.map.tiles[cell.y]?.[cell.x + 1] === Tile.Water ||
            this.map.tiles[cell.y - 1]?.[cell.x] === Tile.Pier ||
            this.map.tiles[cell.y + 1]?.[cell.x] === Tile.Pier ||
            this.map.tiles[cell.y]?.[cell.x - 1] === Tile.Pier ||
            this.map.tiles[cell.y]?.[cell.x + 1] === Tile.Pier);
        const warp = (!paved || softEdge) && !besideWater;
        const ax = cell.x + cell.sx;
        const az = cell.y + cell.sy;
        const bx = cell.x + cell.sx + cell.sw;
        const bz = cell.y + cell.sy + cell.sh;
        const x0 = warp ? cornerX(ax, az) : ax;
        const z0 = warp ? cornerZ(ax, az) : az;
        const x1 = warp ? cornerX(bx, az) : bx;
        const z1 = warp ? cornerZ(bx, az) : az;
        const x2 = warp ? cornerX(bx, bz) : bx;
        const z2 = warp ? cornerZ(bx, bz) : bz;
        const x3 = warp ? cornerX(ax, bz) : ax;
        const z3 = warp ? cornerZ(ax, bz) : bz;
        let yFlat = paved ? cellHeight(this.map, cell.x, cell.y) : 0;
        const amphLift =
          stageScenery && (tile === Tile.Floor || isLawnish(tile))
            ? amphFloorLift(cell.x, cell.y, stageScenery.x, stageScenery.y)
            : 0;
        if (tile === Tile.Floor && stageScenery) {
          yFlat += amphLift;
        }
        let y00: number;
        let y10: number;
        let y11: number;
        let y01: number;
        if (tile === Tile.Water) {
          y00 = waterSurfaceY(ax, az);
          y10 = waterSurfaceY(bx, az);
          y11 = waterSurfaceY(bx, bz);
          y01 = waterSurfaceY(ax, bz);
        } else if (paved) {
          y00 = yFlat;
          y10 = yFlat;
          y11 = yFlat;
          y01 = yFlat;
        } else {
          y00 = terrainHeightAt(this.map, ax, az);
          y10 = terrainHeightAt(this.map, bx, az);
          y11 = terrainHeightAt(this.map, bx, bz);
          y01 = terrainHeightAt(this.map, ax, bz);
          if (stageScenery && isLawnish(tile)) {
            y00 += amphFloorLift(cell.x, cell.y, stageScenery.x, stageScenery.y);
            y10 += amphFloorLift(cell.x + 1, cell.y, stageScenery.x, stageScenery.y);
            y11 += amphFloorLift(cell.x + 1, cell.y + 1, stageScenery.x, stageScenery.y);
            y01 += amphFloorLift(cell.x, cell.y + 1, stageScenery.x, stageScenery.y);
          }
        }
        positions.push(x0, y00, z0, x1, y10, z1, x2, y11, z2, x3, y01, z3);
        if (tile === Tile.Water) {
          // World-space UVs so scrolling reads as drifting highlights across the pond.
          uvs.push(ax * 0.35, az * 0.35, bx * 0.35, az * 0.35, bx * 0.35, bz * 0.35, ax * 0.35, bz * 0.35);
        } else {
          uvs.push(0, 1, 1, 1, 1, 0, 0, 0);
        }
        indices.push(vertex, vertex + 3, vertex + 2, vertex, vertex + 2, vertex + 1);
        vertex += 4;
      }
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
      geometry.setIndex(indices);
      geometry.computeVertexNormals();
      const mapTex =
        tile === Tile.Water ? this.art.tile(tile).clone() : this.art.tile(tile);
      if (tile === Tile.Water) {
        mapTex.wrapS = THREE.RepeatWrapping;
        mapTex.wrapT = THREE.RepeatWrapping;
        mapTex.needsUpdate = true;
      }
      const mesh = new THREE.Mesh(
        geometry,
        new THREE.MeshLambertMaterial({
          map: mapTex,
          polygonOffset: true,
          polygonOffsetFactor:
            tile === Tile.Floor ||
            tile === Tile.Pier ||
            tile === Tile.Sand ||
            tile === Tile.Path ||
            tile === Tile.CourtBasket ||
            tile === Tile.CourtTennis ||
            tile === Tile.CourtLine
              ? -1
              : tile === Tile.Water
                ? 1
                : 0,
          polygonOffsetUnits:
            tile === Tile.Floor ||
            tile === Tile.Pier ||
            tile === Tile.Sand ||
            tile === Tile.Path ||
            tile === Tile.CourtBasket ||
            tile === Tile.CourtTennis ||
            tile === Tile.CourtLine
              ? -1
              : tile === Tile.Water
                ? 1
                : 0,
        }),
      );
      mesh.receiveShadow = false;
      if (tile === Tile.Water) {
        this.waterMesh = mesh;
        this.waterBasePositions = new Float32Array(positions);
      }
      this.worldRoot.add(mesh);
    }

    // Thin court lines from explicit marks (real basketball / tennis layouts).
    this.drawCourtMarks();

    this.drawPathRibbons();
  }

  /** Soft pond drift - scroll highlights; bob only every other frame to save CPU. */
  private updateWater(time: number): void {
    if (!this.waterMesh || !this.waterBasePositions) {
      return;
    }
    const t = time * 0.001;
    const mat = this.waterMesh.material;
    if (mat instanceof THREE.MeshLambertMaterial && mat.map) {
      mat.map.offset.set(t * 0.018, t * 0.012);
    }
    // Skip expensive per-vertex bob on odd frames.
    if ((time | 0) % 2 === 1) {
      return;
    }
    const pos = this.waterMesh.geometry.getAttribute("position") as THREE.BufferAttribute;
    const base = this.waterBasePositions;
    for (let i = 0; i < pos.count; i++) {
      const ix = i * 3;
      const x = base[ix]!;
      const y0 = base[ix + 1]!;
      const z = base[ix + 2]!;
      const bob =
        Math.sin(t * 1.1 + x * 0.55 + z * 0.4) * 0.018 +
        Math.sin(t * 1.7 + x * 0.9 - z * 0.65) * 0.01;
      pos.setY(i, y0 + bob);
    }
    pos.needsUpdate = true;
  }

  private drawCourtMarks(): void {
    const marks = this.map.courtMarks ?? [];
    if (marks.length === 0) {
      return;
    }
    // Thin painted lines - readable up close without chunky toy stripes.
    const LINE_W = 0.12;
    const positions: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];
    let vertex = 0;
    const pushStrip = (
      x0: number,
      z0: number,
      x1: number,
      z1: number,
      x2: number,
      z2: number,
      x3: number,
      z3: number,
      y: number,
    ): void => {
      positions.push(x0, y, z0, x1, y, z1, x2, y, z2, x3, y, z3);
      uvs.push(0, 1, 1, 1, 1, 0, 0, 0);
      // CCW from above so normals point +Y (visible under the park camera).
      indices.push(vertex, vertex + 1, vertex + 2, vertex, vertex + 2, vertex + 3);
      vertex += 4;
    };

    for (const mark of marks) {
      const dx = mark.x1 - mark.x0;
      const dz = mark.y1 - mark.y0;
      const len = Math.hypot(dx, dz);
      if (len < 1e-4) {
        continue;
      }
      const px = (-dz / len) * (LINE_W * 0.5);
      const pz = (dx / len) * (LINE_W * 0.5);
      const mx = (mark.x0 + mark.x1) * 0.5;
      const mz = (mark.y0 + mark.y1) * 0.5;
      // Prefer court asphalt height; fall back to sampled terrain.
      const sampleTile = tileAt(this.map, Math.floor(mx), Math.floor(mz));
      const onCourt =
        sampleTile === Tile.CourtBasket ||
        sampleTile === Tile.CourtTennis ||
        sampleTile === Tile.CourtLine;
      const yLift =
        (onCourt ? cellHeight(this.map, Math.floor(mx), Math.floor(mz)) : terrainHeightAt(this.map, mx, mz)) +
        0.028;
      pushStrip(
        mark.x0 + px,
        mark.y0 + pz,
        mark.x1 + px,
        mark.y1 + pz,
        mark.x1 - px,
        mark.y1 - pz,
        mark.x0 - px,
        mark.y0 - pz,
        yLift,
      );
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    const mesh = new THREE.Mesh(
      geometry,
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: -4,
        polygonOffsetUnits: -4,
        depthWrite: false,
      }),
    );
    mesh.receiveShadow = false;
    mesh.renderOrder = 3;
    this.worldRoot.add(mesh);
  }

  // Skip sidewalk ribbon geometry that crosses travel / spawn pads (avoids dark curb "sticks").
  private pathPointOnTravelPad(x: number, y: number): boolean {
    for (const pad of this.map.teleporters ?? []) {
      if (x >= pad.x - 0.5 && x < pad.x + pad.w + 0.5 && y >= pad.y - 0.5 && y < pad.y + pad.h + 0.5) {
        return true;
      }
    }
    for (const mark of this.map.markers ?? []) {
      if (mark.kind !== "spawn") {
        continue;
      }
      if (x >= mark.x - 0.5 && x < mark.x + mark.w + 0.5 && y >= mark.y - 0.5 && y < mark.y + mark.h + 0.5) {
        return true;
      }
    }
    return false;
  }

  /** Curved park sidewalks as thin continuous ribbon meshes (one draw call per segment). */
  private drawPathRibbons(): void {
    const ribbons = this.map.pathRibbons ?? [];
    if (ribbons.length === 0) {
      return;
    }
    const pathTex = this.art.tile(Tile.Path).clone();
    pathTex.wrapS = THREE.RepeatWrapping;
    pathTex.wrapT = THREE.RepeatWrapping;
    pathTex.needsUpdate = true;
    const mat = new THREE.MeshLambertMaterial({
      map: pathTex,
      color: 0xf3ebe0,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });
    const blockedAt = (x: number, y: number): boolean => {
      const margin = 1.2;
      const x0 = Math.floor(x - margin);
      const x1 = Math.ceil(x + margin);
      const y0 = Math.floor(y - margin);
      const y1 = Math.ceil(y + margin);
      for (let ty = y0; ty <= y1; ty++) {
        for (let tx = x0; tx <= x1; tx++) {
          const tile = this.map.tiles[ty]?.[tx];
          if (
            tile === Tile.Water ||
            tile === Tile.Pier ||
            tile === Tile.CourtBasket ||
            tile === Tile.CourtTennis ||
            tile === Tile.CourtLine
          ) {
            const dx = tx + 0.5 - x;
            const dy = ty + 0.5 - y;
            if (dx * dx + dy * dy <= margin * margin) {
              return true;
            }
          }
        }
      }
      return false;
    };

    const stageScenery = this.map.scenery?.find((s) => s.kind === "stage");

    for (const ribbon of ribbons) {
      if (ribbon.points.length < 2) {
        continue;
      }
      // Split wherever the centerline hits a portal, court, water, or pier.
      const segments: { x: number; y: number }[][] = [];
      let current: { x: number; y: number }[] = [];
      for (const p of ribbon.points) {
        if (this.pathPointOnTravelPad(p.x, p.y) || blockedAt(p.x, p.y)) {
          if (current.length >= 2) {
            segments.push(current);
          }
          current = [];
          continue;
        }
        current.push(p);
      }
      if (current.length >= 2) {
        segments.push(current);
      }

      for (const points of segments) {
        const half = Math.max(0.65, ribbon.width * 0.5);
        const heights = points.map((p) => {
          const tx = Math.floor(p.x);
          const ty = Math.floor(p.y);
          let h = terrainHeightAt(this.map, p.x, p.y) + 0.02;
          if (stageScenery) {
            h += amphFloorLift(tx, ty, stageScenery.x, stageScenery.y);
          }
          return h;
        });
        const face = buildRibbonGeometry(points, half * 0.92, heights, 0.45);
        const faceMesh = new THREE.Mesh(face, mat);
        faceMesh.receiveShadow = true;
        this.worldRoot.add(faceMesh);
      }
    }
  }

  /**
   * Fantasy Town solid plank decks + sparse support posts on Pier tiles.
   * Flat painted Pier mesh stays underneath for collision height; planks sit on top.
   */
  private drawPierDecks(): void {
    const hasPlanks = townTemplates("plank").some((t) => t.id === "planks");
    const hasPillars = townTemplates("pillar").length > 0;
    if (!hasPlanks && !hasPillars) {
      return;
    }
    for (let y = 0; y < this.map.height; y++) {
      for (let x = 0; x < this.map.width; x++) {
        if (this.map.tiles[y]?.[x] !== Tile.Pier) {
          continue;
        }
        const wx = x + 0.5;
        const wz = y + 0.5;
        const hy = cellHeight(this.map, x, y);
        const waterN = this.map.tiles[y - 1]?.[x] === Tile.Water;
        const waterS = this.map.tiles[y + 1]?.[x] === Tile.Water;
        const waterW = this.map.tiles[y]?.[x - 1] === Tile.Water;
        const waterE = this.map.tiles[y]?.[x + 1] === Tile.Water;
        const pierN = this.map.tiles[y - 1]?.[x] === Tile.Pier;
        const pierS = this.map.tiles[y + 1]?.[x] === Tile.Pier;
        const pierW = this.map.tiles[y]?.[x - 1] === Tile.Pier;
        const pierE = this.map.tiles[y]?.[x + 1] === Tile.Pier;
        const ns = (pierN ? 1 : 0) + (pierS ? 1 : 0);
        const ew = (pierW ? 1 : 0) + (pierE ? 1 : 0);
        const alongY = ns >= ew;

        if (hasPlanks) {
          // Solid slab only - skip planks-opening / half (they read as broken frames).
          const deck = cloneTownById("planks");
          if (deck) {
            deck.position.set(wx, hy + 0.02, wz);
            deck.rotation.y = alongY ? 0 : Math.PI / 2;
            deck.scale.multiplyScalar(0.96);
            this.worldRoot.add(deck);
          }
        }

        // Support pilings on water-facing edges — sized to reach the deck underside.
        if (hasPillars && (waterN || waterS || waterW || waterE)) {
          const pillarTemplate = pickTownTemplate("pillar", 0);
          if (pillarTemplate) {
            const pierBottom = hy - 0.02;
            const waterY = -0.22;
            const span = pierBottom - waterY;
            const scaleY = span / pillarTemplate.height;
            const scaleXZ = scaleY * 1.35;
            const edges: { dx: number; dz: number }[] = [];
            if (waterN && !pierN) {
              edges.push({ dx: 0, dz: -0.38 });
            }
            if (waterS && !pierS) {
              edges.push({ dx: 0, dz: 0.38 });
            }
            if (waterW && !pierW) {
              edges.push({ dx: -0.38, dz: 0 });
            }
            if (waterE && !pierE) {
              edges.push({ dx: 0.38, dz: 0 });
            }
            for (const edge of edges) {
              const post = cloneTownProp("pillar", 0);
              if (post) {
                post.position.set(wx + edge.dx, waterY, wz + edge.dz);
                post.scale.set(scaleXZ, scaleY, scaleXZ);
                this.worldRoot.add(post);
              }
            }
          }
        }
      }
    }
  }

  private drawProps(): void {
    this.vegetation?.destroy();
    const batch = new InstancedVegetation();
    this.vegetation = batch;

    for (let y = 0; y < this.map.height; y++) {
      for (let x = 0; x < this.map.width; x++) {
        const tile = this.map.tiles[y]?.[x];
        const jRaw = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
        const jRawB = Math.sin(x * 39.346 + y * 11.135) * 23421.631;
        const j = jRaw - Math.floor(jRaw);
        const jB = jRawB - Math.floor(jRawB);
        const jitter = tile === Tile.Fence ? 0 : 0.78;
        const jx = (j - 0.5) * jitter;
        const jz = (jB - 0.5) * jitter;
        const wx = x + 0.5 + jx;
        const wz = y + 0.5 + jz;
        const hy = terrainHeightAt(this.map, wx, wz);
        if (tile === Tile.Tree) {
          const treeCount = Math.max(1, natureTemplates("tree").length);
          const variant = (x * 13 + y * 7) % treeCount;
          batch.addTree({
            x: wx,
            y: hy,
            z: wz,
            rotY: j * Math.PI * 2,
            // Kenney trees are ~1.2-1.7u tall; park canopy should clearly top humans/dogs.
            scale: vegScale(3.15, j * 0.55),
            variant,
            family: "tree",
            autumn: ((x * 17 + y * 31) % 100) < 38,
          });
          // Occasional companion sapling offsets the lattice.
          if (j > 0.72) {
            const ox = (jB - 0.5) * 0.9;
            const oz = (j - 0.5) * 0.9;
            const bushCount = Math.max(1, natureTemplates("bush").length);
            batch.addBush({
              x: wx + ox,
              y: terrainHeightAt(this.map, wx + ox, wz + oz),
              z: wz + oz,
              rotY: jB * Math.PI * 2,
              scale: vegScale(1.55, j * 0.25),
              variant: Math.floor(j * bushCount),
            });
          }
          // Mini Forest sprout accents under some trees.
          if (jB > 0.78 && natureTemplates("plant").length > 0) {
            const ox = (j - 0.5) * 0.7;
            const oz = (jB - 0.5) * 0.7;
            batch.addPlant({
              x: wx + ox,
              y: terrainHeightAt(this.map, wx + ox, wz + oz),
              z: wz + oz,
              rotY: j * Math.PI * 2,
              scale: vegScale(1.15, jB * 0.15),
              variant: 0,
            });
          }
        } else if (tile === Tile.Fence) {
          this.addProp("fence", wx, wz, hy);
        } else if (tile === Tile.Rock) {
          const rockCount = Math.max(1, natureTemplates("rock").length);
          batch.addRock({
            x: wx,
            y: hy,
            z: wz,
            rotY: j * Math.PI * 2,
            scale: vegScale(1.35, jB * 0.28),
            variant: (x * 5 + y * 11) % rockCount,
          });
        } else if (tile === Tile.Bush) {
          const bushCount = Math.max(1, natureTemplates("bush").length);
          batch.addBush({
            x: wx,
            y: hy,
            z: wz,
            rotY: j * Math.PI * 2,
            scale: vegScale(1.55, jB * 0.28),
            variant: (x * 3 + y * 9) % bushCount,
          });
          if (j > 0.65 && natureTemplates("plant").length > 0) {
            batch.addPlant({
              x: wx + (jB - 0.5) * 0.45,
              y: hy,
              z: wz + (j - 0.5) * 0.45,
              rotY: jB * Math.PI * 2,
              scale: vegScale(1.05, j * 0.12),
              variant: 0,
            });
          }
        } else if (tile === Tile.Flowers) {
          const flowerCount = Math.max(1, natureTemplates("flower").length);
          batch.addFlower({
            x: wx,
            y: hy,
            z: wz,
            rotY: j * Math.PI * 2,
            scale: vegScale(1.25, jB * 0.2),
            variant: Math.floor(j * flowerCount),
          });
          // Occasional second bloom - beds stay airy, not a solid carpet.
          if (j > 0.55) {
            const ox = Math.sin(j * 6.1) * 0.32;
            const oz = Math.cos(jB * 5.3) * 0.32;
            batch.addFlower({
              x: wx + ox,
              y: terrainHeightAt(this.map, wx + ox, wz + oz),
              z: wz + oz,
              rotY: (j + 0.3) * Math.PI * 2,
              scale: vegScale(1.05, jB * 0.18),
              variant: Math.floor((jB + 0.37) * flowerCount),
            });
          }
        } else if (isSidewalkLampCell(this.map, x, y)) {
          this.addProp("lamp", wx, wz, hy);
        } else if (isPathsideBenchCell(this.map, x, y)) {
          this.addProp("bench", wx, wz, hy);
        }
      }
    }

    batch.build();
    this.worldRoot.add(batch.root);
  }

  private placeNodes(): void {
    const stageCast: { id: string; spot: StageActorSpot }[] = [];
    for (const node of this.nodes) {
      this.nodeByTile.set(key(node.x, node.y), node);
      const wx = node.x + 0.5;
      const wz = node.y + 0.5;
      const hy = this.amphAwareGround(node.x, node.y, wx, wz);
      const amphAudience =
        node.id.startsWith("amp-crowd-") ||
        node.id.startsWith("pup-amp-") ||
        node.id === "npc-amp" ||
        node.id === "npc-amp-usher" ||
        node.id === "npc-amp-busker";
      const faceStage = amphAudience ? this.facingTowardStage(wx, wz) : null;
      if (node.kind === "npc") {
        const kind = node.npcKind ?? "guide";
        // Amphitheater audience / staff stay put facing the stage (not roaming).
        if (npcIsWanderer(kind) && !amphAudience) {
          // Flavor-only NPCs roam instead of standing frozen.
          const walker = new ParkNpc(this.art, this.map, node, this.groundY);
          this.parkNpcs.push(walker);
          this.scene.add(walker.group);
          this.interactables.push({
            id: `talk:${node.id}`,
            hit: walker.hit,
            highlight: walker.highlight,
            x: node.x,
            y: node.y,
            w: 1,
            h: 1,
            talkLine: walker.line,
          });
          // Home tile is not a click trap while they wander.
          this.nodeByTile.delete(key(node.x, node.y));
          continue;
        }
        if (kind === "vendor" || kind === "performer") {
          const onStage = kind === "performer";
          const stage = this.map.scenery?.find((s) => s.kind === "stage");
          const stageRoot =
            onStage && stage
              ? cellHeight(this.map, node.x, node.y) +
                amphFloorLift(node.x, node.y, stage.x, stage.y) +
                (stage.yLift ?? 0)
              : hy;
          const standY = onStage ? stageRoot + STAGE_DECK_TOP + 0.02 : hy;
          const variant = Math.floor((node.x * 3.1 + node.y * 7.7) % HUMAN_LOOK_COUNT);
          // Performers face the seating bowl (north of stage); vendors face the stage if in the bowl.
          const performerFacing =
            onStage && stage
              ? Math.atan2(stage.y - 18 + 0.5 - wz, stage.x + 0.5 - wx)
              : faceStage;
          this.addHumanSprite(
            "stand",
            variant,
            wx,
            wz,
            standY,
            node.id,
            node.x,
            node.y,
            performerFacing != null ? { facing: performerFacing } : undefined,
          );
          const sheet = humanSprite("stand", variant);
          const npcNameY = standY + sheet.height + 0.1;
          this.addLabel(node.name, wx, npcNameY, wz, "world-label world-npc", "npc");
          if (kind === "vendor") {
            this.addShopIcon(shopIconForNpc(node), wx, standY + sheet.height + 0.48, wz);
            // Bubble above the name pill so chatter doesn't cover it.
            this.addSocialReactor(node.id, wx, wz, npcNameY + 0.72);
          } else {
            stageCast.push({
              id: node.id,
              spot: {
                x: wx,
                z: wz,
                bubbleY: npcNameY + 1.35,
              },
            });
          }
          continue;
        }
        const size = this.art.dogSize(0, kind);
        this.addNpcDog(
          kind,
          wx,
          wz,
          hy,
          size.width,
          size.height,
          node.id,
          node.x,
          node.y,
          faceStage != null ? { facing: faceStage } : undefined,
        );
        this.addLabel(node.name, wx, hy + size.height + 0.08, wz, "world-label world-npc", "npc");
        if (npcProvidesService(kind)) {
          this.addShopIcon(shopIconForNpc(node), wx, hy + size.height + 0.42, wz);
        }
        continue;
      }
      if (node.kind === "begSpot") {
        const pose: HumanPose = node.begPose ?? "sit";
        const variant = Math.floor((node.x * 5.3 + node.y * 2.9) % HUMAN_LOOK_COUNT);
        const amphCrowd = node.id.startsWith("amp-crowd-");
        const sitGround = this.amphAwareGround(node.x, node.y, wx, wz);
        if (pose === "sit" && !amphCrowd) {
          // Sprite bench under park sitters (amph audience uses picnic blankets).
          this.addFurnitureSprite("bench", wx, wz, sitGround);
        }
        this.addHumanSprite(pose, variant, wx, wz, sitGround, node.id, node.x, node.y, {
          seatY: pose === "sit" ? furnitureSprite("bench").seatY : undefined,
          facing: faceStage ?? undefined,
          interactive: !amphCrowd,
        });
        if (amphCrowd) {
          // Atmosphere only - no names, chatter, or beg during the show.
          this.nodeByTile.delete(key(node.x, node.y));
          continue;
        }
        const sheet = humanSprite(pose, variant);
        const seated = pose === "sit" || pose === "eat";
        const seatLift =
          pose === "sit"
            ? (furnitureSprite("bench").seatY ?? benchSeatY())
            : pose === "eat"
              ? 0.12
              : 0;
        const seatAnchor = sheet.seatAnchor ?? 0;
        // Sprite bottom sits so the seatAnchor meets the bench; head is at bottom + height.
        const spriteBottom = sitGround + seatLift - seatAnchor * sheet.height;
        const headY = seated || pose.startsWith("drum")
          ? spriteBottom + sheet.height
          : sitGround + sheet.height;
        const nameY = headY + 0.22;
        this.addLabel(node.name, wx, nameY, wz, "world-label world-npc", "npc");
        const cookout = pose === "cook" || pose === "eat" || pose === "chat" || node.id.startsWith("bbq-");
        // Bubble sits clearly above the name pill.
        const bubbleY = nameY + 0.72;
        this.addBenchChatter(
          wx,
          bubbleY,
          wz,
          node.x * 13 + node.y * 7,
          cookout ? COOKOUT_CHATTER_LINES : BENCH_CHATTER_LINES,
        );
        this.addSocialReactor(node.id, wx, wz, bubbleY);
        continue;
      }
      const prop: PropKind =
        node.kind === "digSpot" ? "digSpot"
        : node.kind === "fetchSpot" ? "fetchSpot"
        : node.kind === "forageSpot" ? "forageSpot"
        : node.kind === "forge" ? "forge"
        : "bank";
      const pickR =
        node.kind === "forge" || node.kind === "bank" ? 1.1
        : 0.72;
      const model = this.addInteractProp(prop, wx, wz, hy, node.id, node.x, node.y, 1, 1, pickR);
      this.addLabel(node.name, wx, hy + 1.55, wz, "world-label world-node", "node");
      if (node.kind === "forge") {
        this.addShopIcon("hammer", wx, hy + 2.05, wz, "shop", "toycraft");
      } else if (node.kind === "bank") {
        this.addShopIcon("bank", wx, hy + 2.55, wz, "bank");
      } else if (node.kind === "digSpot") {
        const iconEl = this.addShopIcon("shovel", wx, hy + 1.35, wz, "gather", "digging");
        this.gatherVisuals.set(node.id, { root: model, iconEl });
      } else if (node.kind === "fetchSpot") {
        const iconEl = this.addShopIcon("stick", wx, hy + 1.35, wz, "gather", "fetching");
        this.gatherVisuals.set(node.id, { root: model, iconEl });
      } else if (node.kind === "forageSpot") {
        const iconEl = this.addShopIcon("berry", wx, hy + 1.35, wz, "gather", "sniffing");
        this.gatherVisuals.set(node.id, { root: model, iconEl });
      }
    }

    if (stageCast.length > 0) {
      const order = ["npc-hamlet", "npc-juliet", "npc-understudy"];
      stageCast.sort((a, b) => {
        const ai = order.indexOf(a.id);
        const bi = order.indexOf(b.id);
        return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
      });
      this.stageShow?.destroy();
      this.stageShow = new ParkStageShow(stageCast.map((entry) => entry.spot));
      this.worldRoot.add(this.stageShow.root);
    }
  }

  private drawTeleporters(): void {
    for (const pad of this.map.teleporters ?? []) {
      const cx = pad.x + pad.w / 2;
      const cz = pad.y + pad.h / 2;
      const hy = terrainHeightAt(this.map, cx, cz);
      const isMenu = pad.tag === "__TravelMenu__";
      const isPlaza = pad.id === "tp-plaza";
      // District pads sit a hair above Floor so they don't z-fight the tile mesh.
      const lift = isMenu && !isPlaza ? 0.045 : 0;
      const padY = hy + lift;
      const platform = isMenu
        ? makeTravelPortal(Math.min(pad.w, pad.h), isPlaza)
        : makeTravelPad(pad.w, pad.h, false);
      platform.position.set(cx, padY, cz);
      this.worldRoot.add(platform);
      const surfaces = platform.userData.walkSurfaces as
        | { radius: number; surfaceY: number }[]
        | undefined;
      if (surfaces) {
        for (const surface of surfaces) {
          this.walkSurfaces.push({
            x: cx,
            z: cz,
            radius: surface.radius,
            surfaceY: padY + surface.surfaceY,
          });
        }
      }
      if (isMenu) {
        platform.traverse((child) => {
          if (child.userData.portalPulse) {
            this.portalFx.push(child);
          }
        });
      }

      // Flat pads: hover-only rim. Permanent cream hulls z-fight on horizontal tops.
      const highlight = new HoverHighlight({ meshSource: platform, permanentRim: false });
      highlight.group.position.set(cx, padY, cz);
      this.worldRoot.add(highlight.group);

      const pick = makePickVolume(Math.min(pad.w, pad.h) * 0.48, 0.9);
      pick.position.set(cx, padY, cz);
      this.worldRoot.add(pick);

      this.interactables.push({
        id: `pad:${pad.id}`,
        hit: pick,
        highlight,
        x: pad.x,
        y: pad.y,
        w: pad.w,
        h: pad.h,
      });
    }
  }

  private drawMarkers(): void {
    for (const marker of this.map.markers ?? []) {
      const cx = marker.x + marker.w / 2;
      const cz = marker.y + marker.h / 2;
      const hy = terrainHeightAt(this.map, cx, cz);
      const slabW = marker.w * 0.95;
      const slabD = marker.h * 0.95;
      // Inset so cement shows around the circle (not kissing the slab edge).
      const r = Math.min(slabW, slabD) * 0.36;

      // Dark wash under the pad so the slab reads as raised.
      const under = makeContactShadow({
        radius: Math.max(slabW, slabD) * 0.62,
        squash: Math.min(slabW, slabD) / Math.max(slabW, slabD),
        opacity: 0.82,
      });
      under.position.set(cx, hy + 0.012, cz);
      this.worldRoot.add(under);

      // Cement welcome pad - bottom clears Floor (~0.055) so it doesn't z-fight.
      const slabH = 0.1;
      const slabTop = hy + 0.12;
      const slab = new THREE.Mesh(
        new THREE.BoxGeometry(slabW, slabH, slabD),
        new THREE.MeshLambertMaterial({
          color: 0xbdb6ab,
          polygonOffset: true,
          polygonOffsetFactor: -1,
          polygonOffsetUnits: -1,
        }),
      );
      slab.position.set(cx, slabTop - slabH * 0.5, cz);
      slab.receiveShadow = false;
      this.worldRoot.add(slab);
      this.walkSurfaces.push({
        x: cx,
        z: cz,
        radius: Math.min(marker.w, marker.h) * 0.42,
        surfaceY: slabTop,
      });

      // Solid black disc; yellow paw sits on top.
      // Lift + polygonOffset so the disc wins vs the slab (slab uses offset for Floor).
      const disc = new THREE.Mesh(
        new THREE.CircleGeometry(r, 64),
        new THREE.MeshBasicMaterial({
          color: 0x1a1410,
          transparent: true,
          opacity: 0.94,
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -4,
          polygonOffsetUnits: -4,
        }),
      );
      disc.rotation.x = -Math.PI / 2;
      disc.position.set(cx, slabTop + 0.04, cz);
      disc.renderOrder = 1;
      this.worldRoot.add(disc);

      // Yellow paw centered on the black circle (slightly inset so toes stay inside).
      const paw = new THREE.Mesh(
        new THREE.PlaneGeometry(r * 1.05, r * 1.05),
        new THREE.MeshBasicMaterial({
          map: this.art.sprite("spawnPaw"),
          transparent: true,
          depthWrite: false,
          alphaTest: 0.12,
          polygonOffset: true,
          polygonOffsetFactor: -6,
          polygonOffsetUnits: -6,
        }),
      );
      paw.rotation.x = -Math.PI / 2;
      paw.position.set(cx, slabTop + 0.048, cz);
      paw.renderOrder = 2;
      this.worldRoot.add(paw);

      if (marker.label) {
        this.addLabel(marker.label, cx, hy + 0.85, cz, "world-label world-pad", "pad");
      }
    }
  }

  private drawScenery(): void {
    const stage = this.map.scenery?.find((s) => s.kind === "stage");
    for (const item of this.map.scenery ?? []) {
      const wx = item.x + 0.5;
      const wz = item.y + 0.5;
      let hy = cellHeight(this.map, item.x, item.y) + (item.yLift ?? 0);
      if (stage && (item.kind === "stage" || item.id.startsWith("amp-bench"))) {
        hy += amphFloorLift(item.x, item.y, stage.x, stage.y);
      }
      if (isFurnitureSprite(item.kind)) {
        this.addFurnitureSprite(item.kind, wx, wz, hy);
      } else if (item.kind === "stadiumSeat") {
        // Legacy 3D bleacher marker - park amph uses picnic blankets now.
        const face =
          this.facingTowardStage(wx, wz) ??
          item.rotY ??
          Math.atan2(this.map.height * 0.5 - wz, this.map.width * 0.5 - wx);
        this.addFurnitureSprite("bench", wx, wz, hy, { facing: face });
      } else {
        const model = makeProp(item.kind, wx, wz);
        model.position.set(wx, hy, wz);
        if (item.rotY !== undefined) {
          model.rotation.y = item.rotY;
        }
        if (item.scaleX !== undefined) {
          model.scale.x = item.scaleX;
        }
        if (item.localOx) {
          model.translateX(item.localOx);
        }
        if (item.kind === "basketHoop") {
          // Rim faces +local Z. North hoop faces south (0); south faces north (π).
          // Prefer explicit rotY; never use absolute map Y (sports hub is always y>30).
          model.rotation.y =
            item.rotY ??
            (item.id.includes("hoop-s") || item.id.includes("south") ? Math.PI : 0);
        }
        if (item.kind === "tennisNet") {
          // Posts sit on local ±X; rotate so the ribbon spans north-south.
          // Players stand east/west of the net (see ParkTennisMatch).
          model.rotation.y = Math.PI / 2;
        }
        this.worldRoot.add(model);
      }
      if (item.label) {
        this.addLabel(item.label, wx, hy + 1.45, wz, "world-label world-pad", "pad");
      }
    }
  }

  private spawnCritters(): void {
    this.clearCritters();
    for (const def of this.map.critters ?? []) {
      if (def.kind === "duck" && (def.familySize ?? 0) > 0) {
        const family = new ParkDuckFamily(this.map, def, this.groundY);
        this.duckFamilies.push(family);
        this.scene.add(family.root);
        continue;
      }
      if (def.kind === "duckling") {
        continue;
      }
      const critter = new ParkCritter(this.map, def, this.groundY);
      this.critters.push(critter);
      this.scene.add(critter.group);
    }
    for (const def of this.map.leashWalkers ?? []) {
      const walker = new ParkLeashWalker(this.art, this.map, def, this.groundY);
      this.leashWalkers.push(walker);
      this.scene.add(walker.group);
    }
  }

  private clearCritters(): void {
    for (const critter of this.critters) {
      critter.destroy();
    }
    this.critters.length = 0;
    for (const family of this.duckFamilies) {
      family.destroy();
    }
    this.duckFamilies.length = 0;
    for (const npc of this.parkNpcs) {
      npc.destroy();
    }
    this.parkNpcs.length = 0;
    for (const walker of this.leashWalkers) {
      walker.destroy();
    }
    this.leashWalkers.length = 0;
    for (const match of this.tennisMatches) {
      match.destroy();
    }
    this.tennisMatches.length = 0;
    for (const match of this.basketballMatches) {
      match.destroy();
    }
    this.basketballMatches.length = 0;
    for (const circle of this.drumCircles) {
      circle.destroy();
    }
    this.drumCircles.length = 0;
    for (const spot of this.fishingSpots) {
      spot.destroy();
    }
    this.fishingSpots.length = 0;
    for (const game of this.frisbeeGames) {
      game.destroy();
    }
    this.frisbeeGames.length = 0;
    for (const picnic of this.picnics) {
      picnic.destroy();
    }
    this.picnics.length = 0;
    for (const sprayer of this.lawnSprayers) {
      sprayer.destroy();
    }
    this.lawnSprayers.length = 0;
  }

  private spawnTennisMatches(): void {
    let i = 0;
    for (const item of this.map.scenery ?? []) {
      if (item.kind !== "tennisNet") {
        continue;
      }
      const wx = item.x + 0.5;
      const wz = item.y + 0.5;
      const match = new ParkTennisMatch(
        wx,
        wz,
        (x, z) => terrainHeightAt(this.map, x, z),
        i * 9,
        i * 0.37,
      );
      this.tennisMatches.push(match);
      this.worldRoot.add(match.group);
      i += 1;
    }
  }

  private spawnBasketballMatches(): void {
    const hoops = (this.map.scenery ?? []).filter((s) => s.kind === "basketHoop");
    if (hoops.length < 2) {
      return;
    }
    // Sort by map Y so index 0 is north hoop, 1 is south.
    const sorted = [...hoops].sort((a, b) => a.y - b.y);
    const north = sorted[0]!;
    const south = sorted[sorted.length - 1]!;
    const cx = (north.x + south.x) / 2 + 0.5;
    const cz = (north.y + south.y) / 2 + 0.5;
    const match = new ParkBasketballMatch(
      cx,
      cz,
      (x, z) => terrainHeightAt(this.map, x, z),
      { x: north.x + 0.5, z: north.y + 0.5 },
      { x: south.x + 0.5, z: south.y + 0.5 },
      1,
    );
    this.basketballMatches.push(match);
    this.worldRoot.add(match.group);
  }

  private spawnDrumCircles(): void {
    for (const item of this.map.scenery ?? []) {
      if (item.kind !== "drumCircle") {
        continue;
      }
      const circle = new ParkDrumCircle(
        item.x + 0.5,
        item.y + 0.5,
        (x, z) => terrainHeightAt(this.map, x, z),
        8,
      );
      this.drumCircles.push(circle);
      this.worldRoot.add(circle.group);
    }
  }

  private spawnFishingSpots(): void {
    let i = 0;
    for (const item of this.map.scenery ?? []) {
      if (item.kind !== "fishingSpot") {
        continue;
      }
      const spot = new ParkFishing(
        item.x + 0.5,
        item.y + 0.5,
        (x, z) => terrainHeightAt(this.map, x, z),
        item.rotY ?? Math.atan2(1, 0),
        i * 7 + 2,
      );
      this.fishingSpots.push(spot);
      this.worldRoot.add(spot.group);
      i += 1;
    }
  }

  private spawnFrisbeeGames(): void {
    let i = 0;
    for (const item of this.map.scenery ?? []) {
      if (item.kind !== "frisbeeGame") {
        continue;
      }
      const game = new ParkFrisbeeGame(
        item.x + 0.5,
        item.y + 0.5,
        (x, z) => terrainHeightAt(this.map, x, z),
        i * 11,
        i * 0.29,
      );
      this.frisbeeGames.push(game);
      this.worldRoot.add(game.group);
      i += 1;
    }
  }

  private spawnPicnics(): void {
    let i = 0;
    for (const item of this.map.scenery ?? []) {
      if (item.kind !== "picnic") {
        continue;
      }
      const wx = item.x + 0.5;
      const wz = item.y + 0.5;
      const amph = item.id.startsWith("amp-picnic");
      const faceYaw =
        amph
          ? (item.rotY ?? this.facingTowardStage(wx, wz) ?? undefined)
          : undefined;
      const picnic = new ParkPicnic(
        wx,
        wz,
        (x, z) => this.amphAwareGround(Math.floor(x), Math.floor(z), x, z),
        picnicKindFromId(item.id),
        i * 5 + Math.floor(item.x + item.y),
        amph ? { faceYaw, compact: true } : undefined,
      );
      this.picnics.push(picnic);
      this.worldRoot.add(picnic.group);
      i += 1;
    }
  }

  private spawnLawnSprayers(): void {
    let i = 0;
    for (const item of this.map.scenery ?? []) {
      if (item.kind !== "lawnSprayer") {
        continue;
      }
      const sprayer = new ParkLawnSprayer(
        item.x + 0.5,
        item.y + 0.5,
        (x, z) => terrainHeightAt(this.map, x, z),
        i + Math.floor(item.x + item.y),
      );
      this.lawnSprayers.push(sprayer);
      this.worldRoot.add(sprayer.group);
      i += 1;
    }
  }

  private collectDogPoses(): DogPose[] {
    const poses: DogPose[] = [];
    for (const dog of this.dogs.values()) {
      poses.push({ x: dog.poseX, y: dog.poseY });
    }
    for (const npc of this.parkNpcs) {
      poses.push({ x: npc.poseX, y: npc.poseY });
    }
    for (const walker of this.leashWalkers) {
      poses.push({ x: walker.dogPoseX, y: walker.dogPoseY });
    }
    return poses;
  }

  /** Dogs + wandering park people - anything a motion sprayer should notice. */
  private collectMovers(dogs: DogPose[]): DogPose[] {
    const movers = dogs.slice();
    for (const npc of this.parkNpcs) {
      movers.push({ x: npc.poseX, y: npc.poseY });
    }
    for (const walker of this.leashWalkers) {
      movers.push({ x: walker.poseX, y: walker.poseY });
      movers.push({ x: walker.dogPoseX, y: walker.dogPoseY });
    }
    return movers;
  }

  private addProp(kind: PropKind, x: number, z: number, y: number): void {
    if (isFurnitureSprite(kind)) {
      this.addFurnitureSprite(kind, x, z, y);
      return;
    }
    const model = makeProp(kind, x, z);
    model.position.set(x, y, z);
    this.worldRoot.add(model);
    if (kind === "flowers" || kind === "bush") {
      this.swayProps.push({
        root: model,
        x,
        z,
        soft: kind === "flowers",
        baseScale: model.scale.x || 1,
      });
    } else if (kind === "lamp") {
      model.traverse((child) => {
        if (child.userData.ambientGlow) {
          this.portalFx.push(child);
        }
      });
    }
  }

  private addFurnitureSprite(
    kind: FurnitureKind,
    x: number,
    z: number,
    groundY: number,
    opts?: { facing?: number },
  ): void {
    const sheet = furnitureSprite(kind);
    const { width, height, texture } = sheet;
    const facing =
      opts?.facing ?? Math.atan2(this.map.height * 0.5 - z, this.map.width * 0.5 - x);

    const shadow = makeContactShadow({
      radius: Math.min(0.55, width * 0.32),
      opacity: 0.5,
    });
    shadow.position.set(x, groundY + 0.02, z);
    this.worldRoot.add(shadow);

    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(width, height),
      billboardMaterial(texture),
    );
    mesh.position.set(x, groundY + height * SPRITE_FOOT_Y, z);
    mesh.renderOrder = 1;
    this.worldRoot.add(mesh);

    this.billboards.push({
      mesh,
      baseY: groundY + height * SPRITE_FOOT_Y,
      phase: x * 1.3 + z * 0.7,
      bob: false,
      facing,
      worldX: x,
      worldZ: z,
      // Keep benches slightly behind sitters sharing this tile.
      cameraDepth: kind === "bench" ? -0.08 : undefined,
    });
  }

  private addInteractProp(
    kind: PropKind,
    x: number,
    z: number,
    y: number,
    nodeId: string,
    tileX: number,
    tileY: number,
    w: number,
    h: number,
    pickRadius: number,
  ): THREE.Object3D {
    const model = makeProp(kind, x, z);
    model.position.set(x, y, z);
    this.worldRoot.add(model);

    const highlight = new HoverHighlight({ meshSource: model });
    highlight.group.position.set(x, y, z);
    this.worldRoot.add(highlight.group);

    const pick = makePickVolume(pickRadius * 0.95, 1.2);
    pick.position.set(x, y, z);
    this.worldRoot.add(pick);

    this.interactables.push({
      id: `node:${nodeId}`,
      hit: pick,
      highlight,
      x: tileX,
      y: tileY,
      w,
      h,
    });
    return model;
  }

  private addNpcDog(
    kind: NpcKind,
    x: number,
    z: number,
    groundY: number,
    width: number,
    height: number,
    nodeId: string,
    tileX: number,
    tileY: number,
    opts?: { facing?: number },
  ): void {
    const shadow = makeContactShadow({
      radius: Math.min(0.62, width * 0.4),
      squash: 1,
      opacity: 0.88,
    });
    shadow.position.set(x, groundY + 0.02, z);
    this.worldRoot.add(shadow);

    const map = this.art.npcIdle(kind, 0);
    const rim = makeSilhouette(width, height, map);
    rim.position.set(x, groundY + height * SPRITE_FOOT_Y, z);
    this.worldRoot.add(rim);
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(width, height),
      billboardMaterial(map),
    );
    mesh.position.set(x, groundY + height * SPRITE_FOOT_Y, z);
    mesh.renderOrder = 2;
    this.worldRoot.add(mesh);
    const facing =
      opts?.facing ?? Math.atan2(this.map.height * 0.5 - z, this.map.width * 0.5 - x);

    const highlight = new HoverHighlight({
      billboard: { width, height, map },
      facing,
      // Dog textures already bake the cream pup rim.
      permanentRim: false,
    });
    highlight.group.position.set(x, groundY, z);
    this.worldRoot.add(highlight.group);

    const pick = makePickVolume(Math.max(0.55, width * 0.35), height * 0.95);
    pick.position.set(x, groundY, z);
    this.worldRoot.add(pick);

    this.billboards.push({
      mesh,
      rim,
      baseY: groundY + height * SPRITE_FOOT_Y,
      phase: x * 1.7 + z * 0.9,
      bob: false,
      facing,
      worldX: x,
      worldZ: z,
      npcKind: kind,
      frame: 0,
      highlight,
    });
    this.interactables.push({
      id: `node:${nodeId}`,
      hit: pick,
      highlight,
      x: tileX,
      y: tileY,
      w: 1,
      h: 1,
    });
  }

  /** Sitting beggar / cookout guest / standing vendor - canvas sprites. */
  private addHumanSprite(
    pose: HumanPose,
    variant: number,
    x: number,
    z: number,
    groundY: number,
    nodeId: string,
    tileX: number,
    tileY: number,
    opts?: { seatY?: number; facing?: number; interactive?: boolean },
  ): void {
    const sheet = humanSprite(pose, variant);
    const { width, height, texture } = sheet;
    const seated = pose === "sit" || pose === "eat";
    const standing = !seated && !pose.startsWith("drum");
    const benchSeat = opts?.seatY ?? benchSeatY();
    const seatAnchor = sheet.seatAnchor ?? 0;
    const footY = sheet.footY ?? SPRITE_FOOT_Y;
    const interactive = opts?.interactive !== false;
    // Bench sitters: hips on the seat. Picnic eaters: low ground sit. Standers: feet on ground.
    // Never bury the plane under the turf (LPC / tall sit sheets used to sink past ground).
    const rawBottom =
      pose === "sit"
        ? groundY + benchSeat - seatAnchor * height
        : pose === "eat"
          ? groundY + 0.12 - seatAnchor * height
          : groundY;
    const bottomY = seated ? Math.max(groundY - 0.06, rawBottom) : rawBottom;
    const facing =
      opts?.facing ?? Math.atan2(this.map.height * 0.5 - z, this.map.width * 0.5 - x);

    // Amph crowd is dense - hard discs turn into a polka-dot floor. Skip those.
    if (!nodeId.startsWith("amp-crowd-")) {
      const shadow = makeContactShadow({
        radius: Math.min(0.55, width * 0.38),
        squash: 1,
        opacity: seated ? 0.62 : 0.85,
      });
      shadow.position.set(x, (seated ? groundY + 0.04 : bottomY) + 0.02, z);
      this.worldRoot.add(shadow);
    }

    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(width, height),
      billboardMaterial(texture),
    );
    mesh.position.set(x, bottomY + height * footY, z);
    mesh.renderOrder = 3;
    this.worldRoot.add(mesh);

    const highlight = interactive
      ? new HoverHighlight({
          billboard: { width, height, map: texture },
          facing,
          permanentRim: false,
        })
      : null;
    if (highlight) {
      highlight.group.position.set(x, bottomY, z);
      this.worldRoot.add(highlight.group);
    }

    const pick = interactive
      ? makePickVolume(Math.max(0.5, width * 0.38), height * 0.95)
      : null;
    if (pick) {
      pick.position.set(x, bottomY, z);
      this.worldRoot.add(pick);
    }

    this.billboards.push({
      mesh,
      baseY: bottomY + height * footY,
      phase: x * 1.7 + z * 0.9,
      bob: standing,
      facing,
      worldX: x,
      worldZ: z,
      // Sit in front of the bench sprite so depth test doesn't hide the person.
      cameraDepth: pose === "sit" ? 0.14 : pose === "eat" ? 0.06 : undefined,
      highlight: highlight ?? undefined,
    });
    if (interactive && pick && highlight) {
      this.interactables.push({
        id: `node:${nodeId}`,
        hit: pick,
        highlight,
        x: tileX,
        y: tileY,
        w: 1,
        h: 1,
      });
    }
  }

  private addTexturedBillboard(
    texture: THREE.Texture,
    x: number,
    z: number,
    groundY: number,
    width: number,
    height: number,
    bob: boolean,
  ): void {
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(width, height),
      billboardMaterial(texture),
    );
    mesh.position.set(x, groundY + height * SPRITE_FOOT_Y, z);
    this.worldRoot.add(mesh);
    const facing = Math.atan2(this.map.height * 0.5 - z, this.map.width * 0.5 - x);
    this.billboards.push({
      mesh,
      baseY: groundY + height * SPRITE_FOOT_Y,
      phase: x * 1.7 + z * 0.9,
      bob,
      facing,
      worldX: x,
      worldZ: z,
    });
  }

  private addBenchChatter(
    x: number,
    y: number,
    z: number,
    seed: number,
    lines: readonly string[] = BENCH_CHATTER_LINES,
    holdMs = 3200,
  ): void {
    const el = document.createElement("div");
    el.className = "world-label world-bubble world-bubble-sitter";
    el.hidden = true;
    const object = new CSS2DObject(el);
    object.position.set(x, y, z);
    object.visible = false;
    this.worldRoot.add(object);
    // Stagger so the park doesn't chorus at once.
    const delay = 2000 + (Math.abs(seed) % 14000) + Math.random() * 4000;
    this.benchChatters.push({
      el,
      object,
      worldX: x,
      worldZ: z,
      nextAt: performance.now() + delay,
      hideAt: 0,
      seed: Math.abs(seed),
      lines,
      holdMs,
    });
  }

  private addSocialReactor(nodeId: string, x: number, z: number, bubbleY: number): void {
    const el = document.createElement("div");
    el.className = "world-label world-bubble world-bubble-sitter";
    el.hidden = true;
    const object = new CSS2DObject(el);
    object.position.set(x, bubbleY, z);
    object.visible = false;
    this.worldRoot.add(object);
    this.socialReactors.push({
      nodeId,
      el,
      object,
      worldX: x,
      worldZ: z,
      hideAt: 0,
      nextAt: performance.now() + 800 + Math.random() * 2000,
    });
  }

  private updateBenchChatters(now: number): void {
    const focusX = this.focus.x;
    const focusZ = this.focus.z;
    for (const chatter of this.benchChatters) {
      const near = Math.hypot(chatter.worldX - focusX, chatter.worldZ - focusZ) < 22;
      if (chatter.hideAt > 0 && now >= chatter.hideAt) {
        chatter.el.hidden = true;
        chatter.el.textContent = "";
        chatter.object.visible = false;
        chatter.hideAt = 0;
        chatter.nextAt = now + 5000 + (chatter.seed % 7000) + Math.random() * 4000;
        continue;
      }
      if (chatter.hideAt === 0 && now >= chatter.nextAt) {
        if (!near) {
          // Skip this beat if you're far away - try again soon.
          chatter.nextAt = now + 4000 + Math.random() * 4000;
          continue;
        }
        if (this.speechBusyNear(chatter.worldX, chatter.worldZ, now)) {
          // Someone nearby is already talking - wait your turn.
          chatter.nextAt = now + 1800 + Math.random() * 2200;
          continue;
        }
        const pool = chatter.lines.length > 0 ? chatter.lines : BENCH_CHATTER_LINES;
        const line = pool[Math.floor(Math.random() * pool.length)]!;
        chatter.el.textContent = line;
        chatter.el.hidden = false;
        chatter.object.visible = true;
        chatter.hideAt = now + chatter.holdMs + Math.random() * 1400;
      }
    }
  }

  /** True if another idle/social bubble is already up within the exclusive radius. */
  private speechBusyNear(x: number, z: number, now: number, except?: BenchChatter | SocialReactor): boolean {
    for (const other of this.benchChatters) {
      if (other === except || other.hideAt <= now) {
        continue;
      }
      const dx = other.worldX - x;
      const dz = other.worldZ - z;
      if (dx * dx + dz * dz < CHATTER_EXCLUSIVE_R2) {
        return true;
      }
    }
    for (const other of this.socialReactors) {
      if (other === except || other.hideAt <= now) {
        continue;
      }
      const dx = other.worldX - x;
      const dz = other.worldZ - z;
      if (dx * dx + dz * dz < CHATTER_EXCLUSIVE_R2) {
        return true;
      }
    }
    return false;
  }

  private updateSocialReactors(now: number): void {
    const local = this.dogs.get(this.localId);
    if (!local || this.previewMode) {
      return;
    }
    const lx = local.poseX + 0.5;
    const lz = local.poseY + 0.5;
    const friends = this.you?.friendlyNpcs ?? [];
    const wall = Date.now();
    for (const reactor of this.socialReactors) {
      if (reactor.hideAt > 0 && now >= reactor.hideAt) {
        reactor.el.hidden = true;
        reactor.el.classList.remove("world-bubble-pet");
        reactor.el.replaceChildren();
        reactor.object.visible = false;
        reactor.hideAt = 0;
        reactor.nextAt = now + 10_000 + Math.random() * 8000;
        continue;
      }
      if (reactor.hideAt > 0 || now < reactor.nextAt) {
        continue;
      }
      const dist = Math.hypot(reactor.worldX - lx, reactor.worldZ - lz);
      if (dist > 5.2) {
        reactor.nextAt = now + 900 + Math.random() * 900;
        continue;
      }
      if (this.speechBusyNear(reactor.worldX, reactor.worldZ, now, reactor)) {
        reactor.nextAt = now + 1600 + Math.random() * 1800;
        continue;
      }
      const friend = isNpcFriend(friends, reactor.nodeId);
      const mood = npcMoodToward(reactor.nodeId, friend, wall);
      let line: string;
      let petOffer = false;
      if (mood === "cold") {
        // Cool humans often stay quiet instead of snapping every walk-by.
        if (Math.random() > 0.35) {
          reactor.nextAt = now + 6000 + Math.random() * 4000;
          continue;
        }
        line = npcColdLine(reactor.nodeId, wall);
      } else if (mood === "busy") {
        if (Math.random() > 0.45) {
          reactor.nextAt = now + 5000 + Math.random() * 4000;
          continue;
        }
        line = npcBusyLine(reactor.nodeId, wall);
      } else if (npcOffersPet(reactor.nodeId, friend, wall)) {
        petOffer = true;
        line = `${npcHiLine(reactor.nodeId, friend, wall)} ${npcPetOfferLine(reactor.nodeId, wall)}`;
      } else {
        line = npcHiLine(reactor.nodeId, friend, wall);
      }
      reactor.el.classList.toggle("world-bubble-pet", petOffer);
      if (petOffer) {
        const ico = document.createElement("span");
        ico.className = "world-bubble-pet-ico";
        ico.innerHTML = iconSvg("sit", 14);
        const text = document.createElement("span");
        text.className = "world-bubble-pet-text";
        text.textContent = line;
        reactor.el.replaceChildren(ico, text);
      } else {
        reactor.el.textContent = line;
      }
      reactor.el.hidden = false;
      reactor.object.visible = true;
      reactor.hideAt = now + 2600 + Math.random() * 900;
    }
  }

  private addLabel(
    text: string,
    x: number,
    y: number,
    z: number,
    className: string,
    kind: "npc" | "node" | "pad" = "node",
  ): void {
    const el = document.createElement("div");
    el.className = className;
    el.textContent = text;
    const object = new CSS2DObject(el);
    object.position.set(x, y, z);
    this.worldRoot.add(object);
    this.worldLabels.push({ object, kind });
  }

  private addShopIcon(
    kind: string,
    x: number,
    y: number,
    z: number,
    variant: "shop" | "bank" | "gather" = "shop",
    skillId?: SkillId,
  ): HTMLElement {
    const el = document.createElement("div");
    el.className = "world-label world-shop-ico";
    if (variant === "bank") {
      el.classList.add("is-bank");
    } else if (variant === "gather") {
      el.classList.add("is-gather");
    }
    if (skillId) {
      el.classList.add(`skill-outline-${skillId}`, `skill-tint-${skillId}`);
    }
    el.innerHTML = iconSvg(kind, variant === "bank" ? 24 : variant === "gather" ? 15 : 20);
    el.title = variant === "bank" ? "Doggy Bank" : "Interact";
    const object = new CSS2DObject(el);
    object.position.set(x, y, z);
    this.worldRoot.add(object);
    this.worldLabels.push({ object, kind: "ico" });
    return el;
  }

  private applyNodeRuntime(runtime: NodeRuntime[] | undefined): void {
    if (!runtime) {
      return;
    }
    const now = Date.now();
    const next = new Set<string>();
    for (const entry of runtime) {
      if (entry.depletedUntil > now || entry.charges <= 0) {
        next.add(entry.id);
      }
    }
    for (const id of this.depletedNodes) {
      if (!next.has(id)) {
        this.setGatherDepleted(id, false);
      }
    }
    for (const id of next) {
      if (!this.depletedNodes.has(id)) {
        this.setGatherDepleted(id, true);
      }
    }
    this.depletedNodes.clear();
    for (const id of next) {
      this.depletedNodes.add(id);
    }
  }

  private setGatherDepleted(nodeId: string, depleted: boolean): void {
    const visual = this.gatherVisuals.get(nodeId);
    if (!visual) {
      return;
    }
    setObjectOpacity(visual.root, depleted ? 0.38 : 1);
    if (visual.iconEl) {
      visual.iconEl.classList.toggle("is-depleted", depleted);
    }
  }

  private bindInput(): void {
    this.renderer.domElement.addEventListener("pointerdown", this.onPointerDown);
    this.renderer.domElement.addEventListener("pointermove", this.onPointerMove);
    this.renderer.domElement.addEventListener("pointerup", this.onPointerUp);
    this.renderer.domElement.addEventListener("pointercancel", this.onPointerUp);
    this.renderer.domElement.addEventListener("wheel", this.onWheel, { passive: false });
    this.renderer.domElement.addEventListener("contextmenu", this.onContextMenu);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.onWindowBlur);
    this.minimap.runButton.addEventListener("click", this.onRunToggle);
  }

  private onContextMenu = (event: Event): void => {
    event.preventDefault();
  };

  private onRunToggle = (): void => {
    if (this.previewMode) {
      return;
    }
    if (!this.runToggle && this.localEnergy <= 0) {
      return;
    }
    this.runToggle = !this.runToggle;
    this.syncRun();
  };

  private onWindowBlur = (): void => {
    this.spaceDown = false;
    if (this.shiftRun) {
      this.shiftRun = false;
      this.syncRun();
    }
  };

  private isRunning(): boolean {
    return this.shiftRun || this.runToggle;
  }

  /** Kick petals / fireflies when the local pup moves through a flower bed. */
  private tickFlowerStir(
    dtSec: number,
    local: DogActor | undefined,
    wx: number,
    wz: number,
  ): void {
    this.flowerStirIn = Math.max(0, this.flowerStirIn - dtSec);
    if (!local || this.previewMode || !this.ambientVfx) {
      return;
    }
    if (this.flowerStirIn > 0 || !local.hasPath) {
      return;
    }
    const tile = tileAt(this.map, Math.floor(local.poseX), Math.floor(local.poseY));
    if (tile !== Tile.Flowers) {
      return;
    }
    const zoomies = this.isRunning() && this.localEnergy > 0;
    this.ambientVfx.stirFlowerBed(wx, wz, zoomies ? 1.05 : 0.38);
    this.flowerStirIn = zoomies ? 0.16 : 0.34;
  }

  /** Drain / regen stamina locally so the orb updates every frame. */
  private tickLocalEnergy(dtSec: number): void {
    if (this.previewMode) {
      this.minimap.runButton.hidden = true;
      return;
    }
    const wants = this.isRunning();
    const moving = this.dogs.get(this.localId)?.hasPath ?? false;
    if (wants && this.localEnergy > 0 && moving) {
      this.localEnergy = Math.max(0, this.localEnergy - RUN_ENERGY_DRAIN_PER_SEC * dtSec);
      if (this.localEnergy <= 0) {
        this.exhaustRun();
        return;
      }
    } else {
      // Idle (even with run on) or walking recovers; only active jogging drains.
      this.localEnergy = Math.min(RUN_ENERGY_MAX, this.localEnergy + RUN_ENERGY_REGEN_PER_SEC * dtSec);
    }
    this.dogs.get(this.localId)?.setRunning(wants && this.localEnergy > 0);
    this.minimap.setRunActive(this.isRunning());
    this.minimap.setEnergy(this.localEnergy);
    this.minimap.runButton.hidden = false;
  }

  /** Out of stamina - cancel jog intent so energy can regenerate. */
  private exhaustRun(): void {
    this.localEnergy = 0;
    const wasRunning = this.isRunning();
    this.runToggle = false;
    this.shiftRun = false;
    this.dogs.get(this.localId)?.setRunning(false);
    this.minimap.setRunActive(false);
    this.minimap.setEnergy(0);
    this.minimap.runButton.hidden = false;
    if (wasRunning || this.lastSentRunning) {
      this.lastSentRunning = false;
      this.net.send({ type: "setRun", running: false });
    }
  }

  /** Push run intent to server; local speed follows `localEnergy`. */
  private syncRun(force = false): void {
    if (this.isRunning() && this.localEnergy <= 0) {
      this.exhaustRun();
      return;
    }
    const wants = !this.previewMode && this.isRunning();
    const running = wants && this.localEnergy > 0;
    this.dogs.get(this.localId)?.setRunning(running);
    this.minimap.setRunActive(wants);
    this.minimap.setEnergy(this.localEnergy);
    this.minimap.runButton.hidden = this.previewMode;
    if (!force && this.lastSentRunning === wants) {
      return;
    }
    this.lastSentRunning = wants;
    if (!this.previewMode) {
      this.net.send({ type: "setRun", running: wants });
    }
  }

  private sendMove(x: number, y: number): void {
    if (this.isRunning() && this.localEnergy <= 0) {
      this.exhaustRun();
    }
    const wants = !this.previewMode && this.isRunning() && this.localEnergy > 0;
    this.lastSentRunning = wants;
    this.net.send({ type: "move", x, y, running: wants });
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    if (isTyping(event.target)) {
      return;
    }
    if (event.code === "ShiftLeft" || event.code === "ShiftRight") {
      if (!this.shiftRun) {
        if (this.localEnergy <= 0) {
          return;
        }
        this.shiftRun = true;
        this.syncRun();
      }
      return;
    }
    if (event.code !== "Space") {
      return;
    }
    if (!this.previewMode) {
      event.preventDefault();
    }
    this.spaceDown = true;
  };

  private onKeyUp = (event: KeyboardEvent): void => {
    if (event.code === "ShiftLeft" || event.code === "ShiftRight") {
      // Other shift may still be down.
      if (!event.shiftKey && this.shiftRun) {
        this.shiftRun = false;
        this.syncRun();
      }
      return;
    }
    if (event.code === "Space") {
      this.spaceDown = false;
    }
  };

  private onPointerDown = (event: PointerEvent): void => {
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.previewMode) {
      return;
    }
    const pinch = this.pinchDistance();
    if (pinch) {
      this.pinchStartDist = pinch;
      this.pinchStartDistance = this.distance;
      this.orbiting = false;
      this.pendingLeftOrbit = false;
      return;
    }
    this.lastPointerX = event.clientX;
    this.lastPointerY = event.clientY;
    this.downX = event.clientX;
    this.downY = event.clientY;
    this.pointerDirty = false;
    this.pendingLeftOrbit = false;
    if (event.button === 2 || event.button === 1 || this.spaceDown) {
      this.orbiting = true;
      this.renderer.domElement.setPointerCapture(event.pointerId);
    } else if (event.button === 0) {
      // Left drag also orbits; click (no drag) still walks / interacts.
      this.pendingLeftOrbit = true;
      this.renderer.domElement.setPointerCapture(event.pointerId);
    }
  };

  private onPointerMove = (event: PointerEvent): void => {
    if (this.pointers.has(event.pointerId)) {
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    }
    const pinch = this.pinchDistance();
    if (pinch && this.pinchStartDist > 0) {
      this.distance = clamp(
        this.pinchStartDistance * (this.pinchStartDist / pinch),
        CAMERA_DISTANCE_MIN,
        CAMERA_DISTANCE_MAX,
      );
      return;
    }
    if (this.pendingLeftOrbit && !this.orbiting) {
      if (Math.hypot(event.clientX - this.downX, event.clientY - this.downY) > 6) {
        this.orbiting = true;
        this.pendingLeftOrbit = false;
        this.lastPointerX = event.clientX;
        this.lastPointerY = event.clientY;
      }
    }
    if (this.orbiting) {
      const dx = event.clientX - this.lastPointerX;
      const dy = event.clientY - this.lastPointerY;
      this.yaw -= dx * 0.0075;
      this.pitch = clamp(this.pitch + dy * 0.0055, CAMERA_PITCH_MIN, CAMERA_PITCH_MAX);
      this.lastPointerX = event.clientX;
      this.lastPointerY = event.clientY;
      this.pointerDirty = true;
      this.renderer.domElement.style.cursor = "grabbing";
      return;
    }
    if (this.previewMode) {
      this.setHoveredDog(null);
      this.setHoveredInteractable(null);
      this.renderer.domElement.style.cursor = "default";
      return;
    }
    const dog = this.pickDog(event.clientX, event.clientY);
    if (dog) {
      this.setHoveredInteractable(null);
      this.setHoveredDog(dog.playerId);
      this.renderer.domElement.style.cursor = "pointer";
      return;
    }
    this.setHoveredDog(null);
    const target = this.pickInteractable(event.clientX, event.clientY);
    this.setHoveredInteractable(target?.id ?? null);
    this.renderer.domElement.style.cursor = target ? "pointer" : "default";
  };

  private onPointerUp = (event: PointerEvent): void => {
    this.pointers.delete(event.pointerId);
    const wasOrbiting = this.orbiting;
    this.orbiting = false;
    this.pendingLeftOrbit = false;
    this.pinchStartDist = 0;
    this.renderer.domElement.style.cursor = "default";
    if (this.previewMode || event.button !== 0 || wasOrbiting || this.pointerDirty) {
      return;
    }
    if (Math.hypot(event.clientX - this.downX, event.clientY - this.downY) > 6) {
      return;
    }
    this.handleClick(event.clientX, event.clientY);
  };

  private onWheel = (event: WheelEvent): void => {
    if (this.previewMode) {
      return;
    }
    event.preventDefault();
    const factor = event.deltaY > 0 ? 1.08 : 0.92;
    this.distance = clamp(this.distance * factor, CAMERA_DISTANCE_MIN, CAMERA_DISTANCE_MAX);
  };

  private pinchDistance(): number | null {
    if (this.pointers.size < 2) {
      return null;
    }
    const points = [...this.pointers.values()];
    const a = points[0]!;
    const b = points[1]!;
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  private handleClick(clientX: number, clientY: number): void {
    const dog = this.pickDog(clientX, clientY);
    if (dog) {
      this.setHoveredDog(dog.playerId);
      this.clearWaypoint();
      this.setSelectedInteractable(null);
      const dest = nearestInteractPoint(this.map, this.localPose(), {
        x: dog.poseX,
        y: dog.poseY,
      });
      if (dest) {
        const approach = buildContinuousPath(this.map, this.localPose(), dest);
        if (approach.length > 0) {
          this.dogs.get(this.localId)?.predictPath(approach);
        }
      }
      this.net.send({ type: "sniff", playerId: dog.playerId });
      return;
    }

    const target = this.pickInteractable(clientX, clientY);
    if (target) {
      this.clearWaypoint();
      this.setSelectedInteractable(target.id);
      if (target.id.startsWith("talk:") && target.talkLine) {
        const npc = this.parkNpcs.find((entry) => `talk:${entry.nodeId}` === target.id);
        const nodeId = npc?.nodeId ?? target.id.slice(5);
        const destX = npc?.poseX ?? target.x;
        const destY = npc?.poseY ?? target.y;
        const node = this.nodes.find((entry) => entry.id === nodeId);
        const range = node ? interactRangeForNode(node) : NPC_WANDER_INTERACT_RANGE;
        const dest = nearestInteractPoint(
          this.map,
          this.localPose(),
          { x: destX, y: destY },
          range,
        );
        if (dest) {
          const approach = buildContinuousPath(this.map, this.localPose(), dest);
          if (approach.length > 0) {
            this.dogs.get(this.localId)?.predictPath(approach);
          }
        }
        this.net.send({ type: "interact", nodeId });
        return;
      }
      if (target.id.startsWith("node:")) {
        const nodeId = target.id.slice(5);
        const node = this.nodes.find((entry) => entry.id === nodeId);
        if (node) {
          if (isHarvestNode(node)) {
            this.approachHarvestNode(node);
          } else {
            const range = interactRangeForNode(node);
            const dest = nearestInteractPoint(
              this.map,
              this.localPose(),
              { x: node.x, y: node.y },
              range,
            );
            if (dest) {
              const approach = buildContinuousPath(this.map, this.localPose(), dest);
              if (approach.length > 0) {
                this.dogs.get(this.localId)?.predictPath(approach);
              }
            }
            this.net.send({ type: "interact", nodeId: node.id });
          }
          return;
        }
      }
      if (target.id.startsWith("pad:")) {
        const destX = target.x + target.w / 2;
        const destY = target.y + target.h / 2;
        const path = buildContinuousPath(this.map, this.localPose(), { x: destX, y: destY });
        if (path.length > 0) {
          this.dogs.get(this.localId)?.predictPath(path);
        }
        this.setTravelWaypoint(destX, destY, Math.min(target.w, target.h) * 0.48);
        this.sendMove(destX, destY);
        return;
      }
    }

    const ground = this.groundFromClient(clientX, clientY);
    if (!ground) {
      return;
    }

    const node = this.nodeByTile.get(key(ground.tileX, ground.tileY));
    if (node) {
      this.clearWaypoint();
      this.setSelectedInteractable(`node:${node.id}`);
      if (isHarvestNode(node)) {
        this.approachHarvestNode(node);
      } else {
        const range = interactRangeForNode(node);
        const dest = nearestInteractPoint(
          this.map,
          this.localPose(),
          { x: node.x, y: node.y },
          range,
        );
        if (dest) {
          const approach = buildContinuousPath(this.map, this.localPose(), dest);
          if (approach.length > 0) {
            this.dogs.get(this.localId)?.predictPath(approach);
          }
        }
        this.net.send({ type: "interact", nodeId: node.id });
      }
      return;
    }
    if (!isWalkablePos(this.map, ground.poseX, ground.poseY)) {
      // Clicked a bench/lamp/etc. - path to the nearest walkable spot beside it.
      const beside = nearestWalkablePos(this.map, { x: ground.poseX, y: ground.poseY }, 6);
      if (!beside) {
        return;
      }
      this.setSelectedInteractable(null);
      this.setWaypoint(beside.x, beside.y);
      const detour = buildContinuousPath(this.map, this.localPose(), beside);
      if (detour.length === 0) {
        this.hud.toast("Can't find a path there.");
        return;
      }
      this.dogs.get(this.localId)?.predictPath(detour);
      this.sendMove(beside.x, beside.y);
      return;
    }
    this.setSelectedInteractable(null);
    const dest = { x: ground.poseX, y: ground.poseY };
    this.setWaypoint(dest.x, dest.y, ground.surfaceY);
    const path = buildContinuousPath(this.map, this.localPose(), dest);
    if (path.length === 0) {
      this.hud.toast("Can't find a path there.");
      return;
    }
    this.dogs.get(this.localId)?.predictPath(path);
    this.sendMove(dest.x, dest.y);
  }

  private setHoveredDog(playerId: string | null): void {
    if (this.hoveredDogId === playerId) {
      return;
    }
    if (this.hoveredDogId) {
      this.dogs.get(this.hoveredDogId)?.setHighlighted(false);
    }
    this.hoveredDogId = playerId;
    if (playerId) {
      this.dogs.get(playerId)?.setHighlighted(true);
    }
  }

  private setHoveredInteractable(id: string | null): void {
    if (this.hoveredInteractableId === id) {
      return;
    }
    this.hoveredInteractableId = id;
    this.syncInteractHighlights();
  }

  private setSelectedInteractable(id: string | null): void {
    if (this.selectedInteractableId === id) {
      if (id) {
        this.hoveredInteractableId = id;
        this.syncInteractHighlights();
      }
      return;
    }
    this.selectedInteractableId = id;
    if (id) {
      this.hoveredInteractableId = id;
    }
    this.syncInteractHighlights();
  }

  private syncInteractHighlights(): void {
    for (const entry of this.interactables) {
      const on =
        entry.id === this.hoveredInteractableId || entry.id === this.selectedInteractableId;
      entry.highlight.setActive(on);
    }
  }

  private pickDog(clientX: number, clientY: number): DogActor | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return null;
    }
    this.ndc.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.ndc, this.camera);
    const meshes: THREE.Mesh[] = [];
    const byMesh = new Map<THREE.Mesh, DogActor>();
    for (const dog of this.dogs.values()) {
      if (dog.playerId === this.localId) {
        continue;
      }
      meshes.push(dog.hitMesh);
      byMesh.set(dog.hitMesh, dog);
    }
    if (meshes.length === 0) {
      return null;
    }
    const hits = this.raycaster.intersectObjects(meshes, false);
    const hit = hits[0];
    if (!hit) {
      return null;
    }
    return byMesh.get(hit.object as THREE.Mesh) ?? null;
  }

  private pickInteractable(clientX: number, clientY: number): WorldInteractable | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0 || this.interactables.length === 0) {
      return null;
    }
    this.ndc.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.ndc, this.camera);
    const roots = this.interactables.map((entry) => entry.hit);
    const hits = this.raycaster.intersectObjects(roots, true);
    for (const hit of hits) {
      let obj: THREE.Object3D | null = hit.object;
      while (obj) {
        const match = this.interactables.find((entry) => entry.hit === obj);
        if (match) {
          return match;
        }
        obj = obj.parent;
      }
    }

    // Ground-tile fallback (helps on touch / thin props).
    const ground = this.groundFromClient(clientX, clientY);
    if (!ground) {
      return null;
    }
    for (const entry of this.interactables) {
      if (entry.id.startsWith("talk:")) {
        continue;
      }
      if (
        ground.tileX >= entry.x &&
        ground.tileX < entry.x + entry.w &&
        ground.tileY >= entry.y &&
        ground.tileY < entry.y + entry.h
      ) {
        return entry;
      }
    }
    return null;
  }

  private localPose(): { x: number; y: number } {
    const dog = this.dogs.get(this.localId);
    if (dog) {
      return { x: dog.poseX, y: dog.poseY };
    }
    return {
      x: this.you?.x ?? PREVIEW_CAMERA_TILE.x,
      y: this.you?.y ?? PREVIEW_CAMERA_TILE.y,
    };
  }

  /**
   * Pick walkable ground under the cursor by intersecting the height field
   * (pier / plaza / path / pads), not a flat y=0 plane - so the waypoint
   * sits on the surface you're pointing at.
   */
  private groundFromClient(clientX: number, clientY: number): GroundHit | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return null;
    }
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(ndc, this.camera);
    const ray = this.raycaster.ray;
    const hit = this.groundHitScratch;

    const surfaceAt = (x: number, z: number): number =>
      this.amphAwareGround(Math.floor(x), Math.floor(z), x, z);

    // March along the view ray and find where it crosses the terrain height field.
    const maxDist = 320;
    const steps = 96;
    let prevT = 0;
    let prevAbove: boolean | null = null;
    let foundT: number | null = null;
    for (let i = 1; i <= steps; i++) {
      const t = (i / steps) * maxDist;
      ray.at(t, hit);
      if (
        hit.x < -2 ||
        hit.z < -2 ||
        hit.x > this.map.width + 2 ||
        hit.z > this.map.height + 2
      ) {
        prevT = t;
        prevAbove = null;
        continue;
      }
      const hy = surfaceAt(hit.x, hit.z);
      const above = hit.y >= hy;
      if (prevAbove === true && !above) {
        // Binary refine the crossing for a stable paw / flag footing.
        let lo = prevT;
        let hi = t;
        for (let r = 0; r < 10; r++) {
          const mid = (lo + hi) * 0.5;
          ray.at(mid, hit);
          if (hit.y >= surfaceAt(hit.x, hit.z)) {
            lo = mid;
          } else {
            hi = mid;
          }
        }
        foundT = (lo + hi) * 0.5;
        break;
      }
      prevAbove = above;
      prevT = t;
    }

    if (foundT == null) {
      // Fallback: flat plane, then snap Y to the height field at that XZ.
      if (!ray.intersectPlane(this.groundPlane, hit)) {
        return null;
      }
    } else {
      ray.at(foundT, hit);
    }

    const tileX = Math.floor(hit.x);
    const tileY = Math.floor(hit.z);
    if (tileX < 0 || tileY < 0 || tileX >= this.map.width || tileY >= this.map.height) {
      return null;
    }
    const surfaceY = surfaceAt(hit.x, hit.z);
    return {
      worldX: hit.x,
      worldZ: hit.z,
      tileX,
      tileY,
      poseX: hit.x - 0.5,
      poseY: hit.z - 0.5,
      surfaceY,
    };
  }
}

interface GroundHit {
  worldX: number;
  worldZ: number;
  tileX: number;
  tileY: number;
  poseX: number;
  poseY: number;
  /** Exact walkable surface under the click (pier, plaza, path, pad, lawn). */
  surfaceY: number;
}

function key(x: number, y: number): string {
  return `${x},${y}`;
}

function isLawnish(tile: Tile | undefined): boolean {
  return (
    tile === Tile.Grass ||
    tile === Tile.GrassAlt ||
    tile === Tile.Flowers ||
    tile === Tile.Sand ||
    tile === Tile.Tree ||
    tile === Tile.Bush
  );
}

function isHarvestNode(node: ResourceNodeDef): boolean {
  return node.kind === "digSpot" || node.kind === "fetchSpot" || node.kind === "forageSpot";
}

function interactRangeForNode(node: ResourceNodeDef): number {
  if (node.kind === "forge" || node.npcKind === "barksmith") {
    return FORGE_RANGE;
  }
  if (node.kind === "bank" || node.npcKind === "banker") {
    return BANK_RANGE;
  }
  if (node.kind === "npc" && npcIsWanderer(node.npcKind)) {
    return NPC_WANDER_INTERACT_RANGE;
  }
  return INTERACT_RANGE;
}

/** Amphitheater Floor lift: sink stage pad, raise northern seating rings. */
function amphFloorLift(x: number, y: number, stageX: number, stageY: number): number {
  const dx = x - stageX;
  const dy = y - stageY;
  const dist = Math.hypot(dx, dy);
  // Stage platform / apron (around and south of the focus).
  if (Math.abs(dx) <= 20 && y >= stageY - 3 && y <= stageY + 7) {
    return -0.14;
  }
  // Seating bowl north of the stage.
  if (y < stageY - 2 && dist > 9 && dist <= 40 && Math.abs(dx) < 36) {
    let lift = Math.min(0.62, Math.max(0.08, (dist - 9) * 0.024));
    // Flat apron at the north travel pad (tp-amph) — tiered Floor used to hover over lawn.
    const gateY = stageY - 40;
    const rampStart = gateY + 3;
    const rampEnd = gateY + 14;
    if (y <= rampStart) {
      return 0;
    }
    if (y < rampEnd) {
      const t = (y - rampStart) / (rampEnd - rampStart);
      const ease = t * t * (3 - 2 * t);
      lift *= ease;
    }
    return lift;
  }
  return 0;
}

/** Flat triangle-strip ribbon along a centerline (smooth park sidewalk). */
function buildRibbonGeometry(
  points: readonly { x: number; y: number }[],
  halfWidth: number,
  /** Constant height, or one Y per centerline point (follows terrain). */
  y: number | readonly number[],
  /** World units of texture repeat along the walk (slab scale). */
  uvAlong = 0.4,
): THREE.BufferGeometry {
  const n = points.length;
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  let dist = 0;
  const yAt = (i: number): number => (typeof y === "number" ? y : (y[i] ?? y[0] ?? 0));
  // Precompute segment tangents for stable miters (avoids jagged corners).
  const tx: number[] = new Array(n);
  const tz: number[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const prev = points[Math.max(0, i - 1)]!;
    const next = points[Math.min(n - 1, i + 1)]!;
    let dx = next.x - prev.x;
    let dz = next.y - prev.y;
    const len = Math.hypot(dx, dz) || 1;
    tx[i] = dx / len;
    tz[i] = dz / len;
  }
  for (let i = 0; i < n; i++) {
    const p = points[i]!;
    if (i > 0) {
      const prev = points[i - 1]!;
      dist += Math.hypot(p.x - prev.x, p.y - prev.y);
    }
    // Mitered join from adjacent segment normals, clamped so sharp bends don't spike.
    let nx: number;
    let nz: number;
    if (i === 0 || i === n - 1) {
      nx = -(tz[i] ?? 0);
      nz = tx[i] ?? 1;
    } else {
      const n0x = -(tz[i - 1] ?? 0);
      const n0z = tx[i - 1] ?? 1;
      const n1x = -(tz[i] ?? 0);
      const n1z = tx[i] ?? 1;
      nx = n0x + n1x;
      nz = n0z + n1z;
      const nl = Math.hypot(nx, nz);
      if (nl < 1e-4) {
        nx = n1x;
        nz = n1z;
      } else {
        nx /= nl;
        nz /= nl;
        // cos(halfAngle) ≈ dot(normal, segmentNormal); clamp miter length.
        const cos = Math.max(0.35, Math.abs(nx * n1x + nz * n1z));
        const miter = Math.min(halfWidth * 2.2, halfWidth / cos);
        nx *= miter / halfWidth;
        nz *= miter / halfWidth;
      }
    }
    const u = dist * uvAlong;
    const yi = yAt(i);
    positions.push(p.x + nx * halfWidth, yi, p.y + nz * halfWidth);
    positions.push(p.x - nx * halfWidth, yi, p.y - nz * halfWidth);
    uvs.push(0, u, 1, u);
    if (i > 0) {
      const a = (i - 1) * 2;
      const b = a + 1;
      const c = i * 2;
      const d = c + 1;
      indices.push(a, b, d, a, d, c);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function shopIconForNpc(node: ResourceNodeDef): string {
  const kind = node.npcKind;
  if (kind === "banker") {
    return "bank";
  }
  if (kind === "barksmith") {
    return "hammer";
  }
  const id = node.id.toLowerCase();
  if (id.includes("hotdog")) {
    return "hotdog";
  }
  if (id.includes("icecream") || id.includes("cone")) {
    return "cone";
  }
  if (id.includes("pretzel")) {
    return "pretzel";
  }
  if (id.includes("popcorn")) {
    return "popcorn";
  }
  if (id.includes("balloon")) {
    return "balloon";
  }
  return "treat";
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Fade a prop without mutating shared world materials. */
function setObjectOpacity(root: THREE.Object3D, opacity: number): void {
  root.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) {
      return;
    }
    const apply = (material: THREE.Material): THREE.Material => {
      let m = material;
      if (m.userData.shared) {
        m = m.clone();
        m.userData.shared = false;
      }
      m.transparent = opacity < 0.999;
      m.opacity = opacity;
      m.depthWrite = opacity >= 0.999;
      m.needsUpdate = true;
      return m;
    };
    if (Array.isArray(child.material)) {
      child.material = child.material.map(apply);
    } else if (child.material) {
      child.material = apply(child.material);
    }
  });
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
}
